import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, CheckCircle, XCircle, Users, MapPin, Car, Menu, X, AlertCircle, Phone, Upload, Loader, AlertTriangle, Lock, ArrowLeft, ShieldCheck, KeyRound, Settings, Save, Send, Star, MessageCircle, Navigation, Clock } from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { Html5QrcodeScanner } from 'html5-qrcode';
import authServiceDriver from './authServiceDriver';
import BotShield, { botFields, resetBotShield } from './BotShield';
import PhoneVerifyModal from './PhoneVerifyModal'; // V7 : confirmation du numéro par code WhatsApp
import PhoneLoginWeb from './PhoneLoginWeb'; // connexion sans mot de passe « numéro d'abord » (2026-10-30)
import apiService from './apiService';
import CurrentPassengers from './CurrentPassengers';
import IOSInstallBanner from './IOSInstallBanner';

// Composants définis À L'INTÉRIEUR du composant parent (pages, modales) : leur « type » est
// une nouvelle fonction à chaque rendu du parent → React les démonte et les remonte, ce qui
// vide les formulaires en cours de saisie (ex. à chaque poll de 30 s). <Stable render={X} />
// garde un type constant (Stable) tout en exécutant la dernière version de X : l'état interne
// (useState) survit aux rendus du parent, les fermetures restent fraîches.
function Stable({ render, ...props }) { return render(props); }


// Plan mapping avec noms et couleurs
const PLAN_MAPPING = {
  basic: { name: 'BASIQUE', color: '#10b981' },
  'private-basic': { name: 'PRIVÉ BASIQUE', color: '#FF7800' },
  standard: { name: 'STANDARD', color: '#FFB900' },
  premium: { name: 'PREMIUM', color: '#FF9B00' },
  university: { name: 'UNIVERSITÉ', color: '#06b6d4' },
  elite: { name: 'ÉLITE', color: '#dc2626' }
};

export default function TaxiDriverApp() {
  const [currentPage, setCurrentPage] = useState('login');
  // « Devenir chauffeur » (2026-10-30) : compte utilisateur connecté par code, sans profil chauffeur
  const [onboardUser, setOnboardUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [language, setLanguage] = useState('fr');
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeRoute, setActiveRoute] = useState(null);
  const [passengerCount, setPassengerCount] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [, setCameraPermission] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [scanStats, setScanStats] = useState({ today: 0, week: 0, month: 0, year: 0, total: 0 });
  const [scanFilter, setScanFilter] = useState('today');
  const [driverRoutes, setDriverRoutes] = useState([]);
  const [loadingDriverRoutes, setLoadingDriverRoutes] = useState(false);
  const html5QrcodeRef = useRef(null);
  
  // ===== NOTIFICATION COURSES TAXI (global) =====
  const [pendingRidesCount, setPendingRidesCount] = useState(0);
  const [showRideNotification, setShowRideNotification] = useState(false);
  const [notificationRide, setNotificationRide] = useState(null);
  const lastKnownRideIdsRef = useRef(new Set());
  const rideNotifPollRef = useRef(null);
  const audioContextRef = useRef(null);
  const rateLimitBackoffRef = useRef(false); // Stop polling on 429
  const [taxiTab, setTaxiTab] = useState('available'); // available, active, history — at parent level to survive re-renders
  const [isDriverBlocked, setIsDriverBlocked] = useState(false);
  const [driverBlockedDebt, setDriverBlockedDebt] = useState(0);
  const [isDriverAvailable, setIsDriverAvailable] = useState(false);
  const [togglingAvailability, setTogglingAvailability] = useState(false);

  // Mode actif : 'driver' (chauffeur) ou 'deliverer' (livreur)
  // Visible uniquement si approved_roles contient les deux
  const [activeMode, setActiveMode] = useState(() => localStorage.getItem('driverActiveMode') || 'driver');
  const switchMode = (mode) => {
    setActiveMode(mode);
    localStorage.setItem('driverActiveMode', mode);
  };

  // Son de notification (bip-bip avec Web Audio API)
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const now = ctx.currentTime;

      // 3 bips courts montants
      [0, 0.2, 0.4].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = 800 + (i * 200); // 800, 1000, 1200 Hz
        gain.gain.setValueAtTime(0.3, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.15);
        osc.start(now + delay);
        osc.stop(now + delay + 0.15);
      });
    } catch (e) {
      console.log('Audio non supporté');
    }
  }, []);

  // Vibration
  const vibrateDevice = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }
  }, []);

  // Toggle disponibilité ON/OFF
  const toggleAvailability = useCallback(async (forceValue) => {
    if (!currentUser?.driver_id || togglingAvailability) return;
    setTogglingAvailability(true);
    try {
      const newVal = typeof forceValue === 'boolean' ? forceValue : !isDriverAvailable;
      const res = await apiService.patch(`/drivers/${currentUser.driver_id}/availability`, { is_available: newVal }, { userType: 'driver' });
      if (res.success) {
        setIsDriverAvailable(res.is_available);
        if (!res.is_available) {
          setPendingRidesCount(0);
          setShowRideNotification(false);
        }
      }
    } catch (err) {
      console.error('Erreur toggle dispo:', err);
    }
    setTogglingAvailability(false);
  }, [currentUser?.driver_id, isDriverAvailable, togglingAvailability]);

  // Polling global des courses disponibles
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.driver_id || currentUser?.status !== 'approved') {
      return;
    }

    // Si le chauffeur est hors ligne, pas de polling
    if (!isDriverAvailable) {
      setPendingRidesCount(0);
      return;
    }

    const checkNewRides = async () => {
      // Skip if rate limited or offline
      if (rateLimitBackoffRef.current || !isDriverAvailable) return;
      try {
        const res = await apiService.get('/taxi/available', { userType: 'driver' });
        if (res.success && res.rides) {
          const rides = res.rides || [];
          setPendingRidesCount(rides.length);

          // Détecter les NOUVELLES courses
          const currentIds = new Set(rides.map(r => r.ride_id));
          const newRides = rides.filter(r => !lastKnownRideIdsRef.current.has(r.ride_id));

          if (newRides.length > 0 && lastKnownRideIdsRef.current.size > 0) {
            // Nouvelle course détectée ! Notifier
            setNotificationRide(newRides[0]);
            setShowRideNotification(true);
            playNotificationSound();
            vibrateDevice();

            // Auto-hide après 15 secondes
            setTimeout(() => setShowRideNotification(false), 15000);
          }

          // Même au premier chargement, si on a des courses, montrer le badge
          if (rides.length > 0 && lastKnownRideIdsRef.current.size === 0 && currentPage !== 'taxi-driver') {
            setNotificationRide(rides[0]);
            setShowRideNotification(true);
            playNotificationSound();
            vibrateDevice();
            setTimeout(() => setShowRideNotification(false), 15000);
          }

          lastKnownRideIdsRef.current = currentIds;
        }
      } catch (err) {
        // Backoff on 429
        if (err.status === 429) {
          rateLimitBackoffRef.current = true;
          setTimeout(() => { rateLimitBackoffRef.current = false; }, 60000);
        }
      }
    };

    // Vérifier immédiatement
    checkNewRides();
    // Puis toutes les 30 secondes
    rideNotifPollRef.current = setInterval(checkNewRides, 30000);

    return () => {
      if (rideNotifPollRef.current) clearInterval(rideNotifPollRef.current);
    };
  }, [isAuthenticated, currentUser?.driver_id, currentUser?.status, currentPage, playNotificationSound, vibrateDevice, isDriverAvailable]);

  // Naviguer vers les courses taxi depuis la notification
  const goToTaxiFromNotification = useCallback(() => {
    setShowRideNotification(false);
    setCurrentPage('taxi-driver');
    setMenuOpen(false);
  }, []);

  // Récupérer les statistiques de scans
  const fetchScanStats = async (driverId) => {
    if (rateLimitBackoffRef.current) return;
    try {
      const data = await apiService.get(`/drivers/${driverId}/stats`, { userType: 'driver' });
      if (data.success && data.stats) {
        setScanStats(data.stats);
      }
    } catch (error) {
      if (error.status === 429) {
        rateLimitBackoffRef.current = true;
        setTimeout(() => { rateLimitBackoffRef.current = false; }, 60000);
      }
      console.error('Erreur récupération stats scans:', error);
    }
  };

  // Récupérer le nombre de passagers actuels
  const fetchCurrentPassengersCount = async (driverId) => {
    if (rateLimitBackoffRef.current) return;
    try {
      const data = await apiService.get(`/trips/active/${driverId}`, { userType: 'driver' });
      if (data.success) {
        setPassengerCount(data.count || 0);
      }
    } catch (error) {
      if (error.status === 429) {
        rateLimitBackoffRef.current = true;
        setTimeout(() => { rateLimitBackoffRef.current = false; }, 60000);
      }
      console.error('Erreur récupération passagers actuels:', error);
    }
  };
  
  // Vérifier si le compte chauffeur est bloqué
  const checkDriverBlocked = async (driverId) => {
    try {
      const res = await apiService.get(`/balance/driver/${driverId}`, { userType: 'driver' });
      if (res.success && res.balance?.account_blocked) {
        setIsDriverBlocked(true);
        setDriverBlockedDebt(Math.abs(res.balance?.current_balance || 0));
      } else {
        setIsDriverBlocked(false);
      }
    } catch (err) {
      // silently ignore
    }
  };

  // Connexion par téléphone (2026-10-30) : session de 180 jours → reprise sans code à l'ouverture
  React.useEffect(() => {
    if (authServiceDriver.isAuthenticated() || !authServiceDriver.hasRefresh()) return;
    authServiceDriver.restoreSession().then((r) => {
      if (!r?.success) return;
      const user = r.user;
      setCurrentUser(user);
      setIsAuthenticated(true);
      setIsDriverAvailable(user.is_available || false);
      if (user.status === 'pending') setCurrentPage('pending');
      else {
        setCurrentPage('home');
        if (user.driver_id) { fetchScanStats(user.driver_id); fetchCurrentPassengersCount(user.driver_id); checkDriverBlocked(user.driver_id); }
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Vérifier si le chauffeur est déjà connecté au chargement
  React.useEffect(() => {
    let scanInterval = null;
    let passengerInterval = null;

    if (authServiceDriver.isAuthenticated()) {
      const user = authServiceDriver.getCurrentUser();
      setCurrentUser(user);
      setIsAuthenticated(true);
      setIsDriverAvailable(user.is_available || false);
      
      if (user.status === 'pending') {
        setCurrentPage('pending');
      } else if (user.status === 'approved') {
        setCurrentPage('home');
        if (user.driver_id) {
          fetchScanStats(user.driver_id);
          fetchCurrentPassengersCount(user.driver_id);
          checkDriverBlocked(user.driver_id);
          // Rafraîchir stats scans toutes les 2 minutes
          scanInterval = setInterval(() => fetchScanStats(user.driver_id), 120000);
          // Rafraîchir nombre passagers toutes les 60 secondes
          passengerInterval = setInterval(() => fetchCurrentPassengersCount(user.driver_id), 60000);
        }
      }

      // Rafraîchir le profil depuis le backend pour avoir approved_roles à jour
      // (identique à AppContext.loadDriver dans l'app native)
      if (user.driver_id) {
        apiService.get(`/drivers/${user.driver_id}`, { userType: 'driver' })
          .then(res => {
            const fresh = res?.driver || res;
            if (fresh?.driver_id || fresh?.name) {
              const merged = { ...user, ...fresh };
              localStorage.setItem('currentDriver', JSON.stringify(merged));
              setCurrentUser(merged);
              setIsDriverAvailable(merged.is_available ?? user.is_available ?? false);
              // Corriger le mode actif si nécessaire
              const roles = merged.approved_roles || [];
              const savedMode = localStorage.getItem('driverActiveMode');
              if (roles.length > 0 && savedMode && !roles.includes(savedMode)) {
                setActiveMode(roles[0]);
                localStorage.setItem('driverActiveMode', roles[0]);
              }
              // Réorienter si le statut a changé
              if (merged.status === 'pending') setCurrentPage('pending');
              else if (merged.status === 'approved' && user.status === 'pending') setCurrentPage('home');
            }
          })
          .catch(() => {});
      }
    }

    // Cleanup function toujours retournée, peu importe les conditions
    return () => {
      if (scanInterval) clearInterval(scanInterval);
      if (passengerInterval) clearInterval(passengerInterval);
    };
  }, []);

  // Mettre à jour le nombre de passagers quand on change de page ou de currentUser
  React.useEffect(() => {
    if (currentUser?.driver_id && (currentPage === 'home' || currentPage === 'currentPassengers')) {
      fetchCurrentPassengersCount(currentUser.driver_id);
    }
  }, [currentPage, currentUser?.driver_id]);

  const translations = {
    fr: {
      home: 'Accueil',
      scanner: 'Scanner',
      myRoute: 'Ma Ligne',
      stats: 'Statistiques',
      settings: 'Paramètres',
      welcome: 'Bienvenue',
      scanQR: 'Scanner QR Code',
      passengersOnBoard: 'Passagers à bord',
      todayEarnings: 'Gains du jour',
      tripsToday: 'Trajets aujourd\'hui',
      scanPassenger: 'Scanner le passager',
      tapToScan: 'Appuyez pour scanner',
      validSubscription: 'Abonnement valide',
      invalidSubscription: 'Abonnement invalide',
      expired: 'Expiré',
      notAuthorized: 'Non autorisé pour cette ligne',
      passengerName: 'Nom du passager',
      subscriptionType: 'Type d\'abonnement',
      expiryDate: 'Date d\'expiration',
      acceptPassenger: 'Accepter',
      refusePassenger: 'Refuser',
      scanAnother: 'Scanner un autre',
      selectRoute: 'Sélectionner une ligne',
      startRoute: 'Démarrer la ligne',
      endRoute: 'Terminer la ligne',
      routeActive: 'Ligne active',
      availableSeats: 'Places disponibles',
      totalSeats: 'Places totales',
      currentPassengersTitle: 'Passagers actuellement à bord',
      today: 'Aujourd\'hui',
      thisWeek: 'Cette semaine',
      thisMonth: 'Ce mois',
      scansToday: 'Scans aujourd\'hui',
      scansThisWeek: 'Scans cette semaine',
      scansThisMonth: 'Scans ce mois',
      taxiRides: 'Courses Taxi',
      availableRides: 'Courses disponibles',
      noAvailableRides: 'Aucune course disponible',
      driverOnline: 'En ligne',
      driverOffline: 'Hors ligne',
      goOnline: 'Se mettre en ligne',
      goOffline: 'Se mettre hors ligne',
      offlineMessage: 'Vous êtes hors ligne. Activez votre disponibilité pour recevoir des courses.',
      activeRide: 'Course en cours',
      rideHistory: 'Historique des courses',
      accept: 'Accepter',
      decline: 'Refuser',
      arriving: 'Je suis arrivé',
      startRide: 'Démarrer la course',
      endRide: 'Terminer la course',
      cancelRide: 'Annuler la course',
      myBalance: 'Mon Solde',
      currentBalance: 'Solde actuel',
      weekSummary: 'Résumé de la semaine',
      rides: 'Courses',
      earnings: 'Gains',
      commissions: 'Commissions',
      nextPayout: 'Prochain virement',
      accountBlocked: 'Compte bloqué',
      transactionHistory: 'Historique',
      period: 'Période',
      week: 'Semaine',
      month: 'Mois',
      year: 'Année',
      all: 'Tout',
    },
    en: {
      home: 'Home',
      scanner: 'Scanner',
      myRoute: 'My Route',
      stats: 'Statistics',
      settings: 'Settings',
      welcome: 'Welcome',
      scanQR: 'Scan QR Code',
      passengersOnBoard: 'Passengers on board',
      todayEarnings: 'Today\'s earnings',
      tripsToday: 'Trips today',
      scanPassenger: 'Scan passenger',
      tapToScan: 'Tap to scan',
      validSubscription: 'Valid subscription',
      invalidSubscription: 'Invalid subscription',
      expired: 'Expired',
      notAuthorized: 'Not authorized for this route',
      passengerName: 'Passenger name',
      subscriptionType: 'Subscription type',
      expiryDate: 'Expiry date',
      acceptPassenger: 'Accept',
      refusePassenger: 'Refuse',
      scanAnother: 'Scan another',
      selectRoute: 'Select a route',
      startRoute: 'Start route',
      endRoute: 'End route',
      routeActive: 'Route active',
      availableSeats: 'Available seats',
      totalSeats: 'Total seats',
      currentPassengersTitle: 'Passengers currently on board',
      today: 'Today',
      thisWeek: 'This week',
      thisMonth: 'This month',
      scansToday: 'Scans today',
      scansThisWeek: 'Scans this week',
      scansThisMonth: 'Scans this month',
      taxiRides: 'Taxi Rides',
      availableRides: 'Available rides',
      noAvailableRides: 'No rides available',
      driverOnline: 'Online',
      driverOffline: 'Offline',
      goOnline: 'Go online',
      goOffline: 'Go offline',
      offlineMessage: 'You are offline. Turn on availability to receive ride requests.',
      activeRide: 'Active ride',
      rideHistory: 'Ride history',
      accept: 'Accept',
      decline: 'Decline',
      arriving: 'I have arrived',
      startRide: 'Start ride',
      endRide: 'End ride',
      cancelRide: 'Cancel ride',
      myBalance: 'My Balance',
      currentBalance: 'Current balance',
      weekSummary: 'Week summary',
      rides: 'Rides',
      earnings: 'Earnings',
      commissions: 'Commissions',
      nextPayout: 'Next payout',
      accountBlocked: 'Account blocked',
      transactionHistory: 'History',
      period: 'Period',
      week: 'Week',
      month: 'Month',
      year: 'Year',
      all: 'All',
    },
    sw: {
      home: 'Nyumbani',
      scanner: 'Skanari',
      myRoute: 'Njia Yangu',
      stats: 'Takwimu',
      settings: 'Mipangilio',
      welcome: 'Karibu',
      scanQR: 'Scan QR Code',
      passengersOnBoard: 'Abiria ndani',
      todayEarnings: 'Mapato ya leo',
      tripsToday: 'Safari za leo',
      scanPassenger: 'Scan mwananchi',
      tapToScan: 'Bofya kuscan',
      validSubscription: 'Usajili halali',
      invalidSubscription: 'Usajili si halali',
      expired: 'Imeisha',
      notAuthorized: 'Hairuhusiwi kwa njia hii',
      passengerName: 'Jina la mwananchi',
      subscriptionType: 'Aina ya usajili',
      expiryDate: 'Tarehe ya kumalizika',
      acceptPassenger: 'Kubali',
      refusePassenger: 'Kataa',
      scanAnother: 'Scan mwingine',
      selectRoute: 'Chagua njia',
      startRoute: 'Anza njia',
      endRoute: 'Maliza njia',
      routeActive: 'Njia inafanya kazi',
      availableSeats: 'Viti vilivyopo',
      totalSeats: 'Viti vyote',
      currentPassengersTitle: 'Abiria sasa wanakaa',
      today: 'Leo',
      thisWeek: 'Juma hili',
      thisMonth: 'Mwezi huu',
      scansToday: 'Scans leo',
      scansThisWeek: 'Scans juma hili',
      scansThisMonth: 'Scans mwezi huu',
      taxiRides: 'Safari za Teksi',
      availableRides: 'Safari zinazopatikana',
      noAvailableRides: 'Hakuna safari',
      driverOnline: 'Mtandaoni',
      driverOffline: 'Nje ya mtandao',
      goOnline: 'Ingia mtandaoni',
      goOffline: 'Ondoka mtandaoni',
      offlineMessage: 'Uko nje ya mtandao. Washa upatikanaji kupokea safari.',
      activeRide: 'Safari inayoendelea',
      rideHistory: 'Historia ya safari',
      accept: 'Kubali',
      decline: 'Kataa',
      arriving: 'Nimefika',
      startRide: 'Anza safari',
      endRide: 'Maliza safari',
      cancelRide: 'Sitisha safari',
      myBalance: 'Salio Langu',
      currentBalance: 'Salio la sasa',
      weekSummary: 'Muhtasari wa juma',
      rides: 'Safari',
      earnings: 'Mapato',
      commissions: 'Kamisheni',
      nextPayout: 'Malipo yajayo',
      accountBlocked: 'Akaunti imefungwa',
      transactionHistory: 'Historia',
      period: 'Kipindi',
      week: 'Juma',
      month: 'Mwezi',
      year: 'Mwaka',
      all: 'Yote',
    },
    ln: {
      home: 'Ndako',
      scanner: 'Scanner',
      myRoute: 'Nzela na ngai',
      stats: 'Ba statistiques',
      settings: 'Ba paramètres',
      welcome: 'Mbote',
      scanQR: 'Scan QR Code',
      passengersOnBoard: 'Bato na kati',
      todayEarnings: 'Mbongo ya lelo',
      tripsToday: 'Mibembo ya lelo',
      scanPassenger: 'Scan moto',
      tapToScan: 'Fina mpo na scan',
      validSubscription: 'Abonnement ezali malamu',
      invalidSubscription: 'Abonnement ezali malamu te',
      expired: 'Esilaki',
      notAuthorized: 'Azali na ndingisa te mpo na nzela oyo',
      passengerName: 'Nkombo ya moto',
      subscriptionType: 'Lolenge ya abonnement',
      expiryDate: 'Mokolo ya kosila',
      acceptPassenger: 'Ndima',
      refusePassenger: 'Boyi',
      scanAnother: 'Scan mosusu',
      selectRoute: 'Pona nzela',
      startRoute: 'Banda nzela',
      endRoute: 'Silisa nzela',
      routeActive: 'Nzela ezali kosala',
      availableSeats: 'Bakiti oyo ezali',
      totalSeats: 'Bakiti nyonso',
      currentPassengersTitle: 'Bato oyo bazali na kati sika',
      today: 'Lelo',
      thisWeek: 'Mufumfu wa lelo',
      thisMonth: 'Sanza ya lelo',
      scansToday: 'Scans lelo',
      scansThisWeek: 'Scans mufumfu wa lelo',
      scansThisMonth: 'Scans sanza ya lelo',
      taxiRides: 'Ba courses ya Taxi',
      availableRides: 'Ba courses oyo ezali',
      noAvailableRides: 'Courses ezali te',
      driverOnline: 'En ligne',
      driverOffline: 'Hors ligne',
      goOnline: 'Kota en ligne',
      goOffline: 'Bima en ligne',
      offlineMessage: 'Ozali hors ligne. Fungola disponibilité na yo mpo ozwa ba courses.',
      activeRide: 'Course oyo ezali kokende',
      rideHistory: 'Historique ya ba courses',
      accept: 'Ndima',
      decline: 'Boyi',
      arriving: 'Nakomi',
      startRide: 'Banda course',
      endRide: 'Silisa course',
      cancelRide: 'Tika course',
      myBalance: 'Solde na ngai',
      currentBalance: 'Solde ya sika',
      weekSummary: 'Résumé ya poso',
      rides: 'Ba courses',
      earnings: 'Mbongo',
      commissions: 'Commissions',
      nextPayout: 'Virement oyo ekoya',
      accountBlocked: 'Compte ekangami',
      transactionHistory: 'Historique',
      period: 'Eleko',
      week: 'Poso',
      month: 'Sanza',
      year: 'Mobu',
      all: 'Nyonso',
    }
  };

  const t = translations[language];

  const driver = {
    name: currentUser?.name || 'Mulamba Joseph',
    vehicle: currentUser?.vehicle?.plate || 'AB 234 KN',
    rating: currentUser?.rating ?? 5,
    rating_count: currentUser?.rating_count ?? 0,
    earnings: '45,000',
    trips: currentUser?.trips || 8
  };

  // Charger les routes depuis l'API pour le chauffeur
  const loadDriverRoutes = async () => {
    try {
      setLoadingDriverRoutes(true);
      const data = await apiService.request('/routes?active=true');
      if (data.success && data.routes) {
        setDriverRoutes(data.routes);
      }
    } catch (error) {
      console.error('Erreur chargement routes chauffeur:', error);
    } finally {
      setLoadingDriverRoutes(false);
    }
  };

  // Charger les routes quand on affiche la page route
  React.useEffect(() => {
    if (currentPage === 'route' && driverRoutes.length === 0 && isAuthenticated) {
      loadDriverRoutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isAuthenticated]);

  // Initialize real QR code scanner with camera
  const initializeScanner = async () => {
    try {
      setCameraError(null);
      
      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission(true);
      
      // Mettre l'état isScanning à true EN PREMIER pour que le DOM se rende
      setIsScanning(true);
      
      // Attendre que le DOM soit rendu avant d'initialiser le scanner
      setTimeout(() => {
        try {
          // Initialize Html5QrcodeScanner
          const config = {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            rememberLastUsedCamera: true,
            showTorchButtonIfSupported: true,
            defaultZoomValueIfSupported: 2
          };
          
          const html5QrcodeScanner = new Html5QrcodeScanner(
            'qr-reader',
            config,
            false
          );
          
          html5QrcodeRef.current = html5QrcodeScanner;
          
          html5QrcodeScanner.render(
            (decodedText) => {
              // QR code found and decoded
              processScanResult(decodedText);
              html5QrcodeScanner.clear();
            },
            (errorMessage) => {
              // Handle scan error silently (scanner still looking)
            }
          );
        } catch (scanError) {
          console.error('❌ Erreur lors de l\'initialisation du scanner:', scanError);
          setIsScanning(false);
          setCameraError('Erreur lors du démarrage du scanner: ' + (scanError?.message || JSON.stringify(scanError)));
        }
      }, 100); // Attendre 100ms pour que le DOM soit rendu
      
    } catch (error) {
      console.error('❌ Erreur scanner:', error);
      
      if (error.name === 'NotAllowedError') {
        setCameraError('Accès caméra refusé. Vérifiez les permissions du navigateur.');
        setCameraPermission(false);
      } else if (error.name === 'NotFoundError') {
        setCameraError('Aucune caméra trouvée sur ce dispositif.');
        setCameraPermission(false);
      } else if (error.name === 'NotReadableError') {
        setCameraError('La caméra est déjà utilisée par une autre application.');
        setCameraPermission(false);
      } else {
        const errorMsg = error?.message || JSON.stringify(error) || 'Erreur inconnue';
        setCameraError('Erreur lors de l\'accès à la caméra: ' + errorMsg);
      }
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.clear();
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
      html5QrcodeRef.current = null;
    }
    setIsScanning(false);
  };

  const processScanResult = async (qrCodeText) => {
    // Validate QR code format and fetch passenger data from backend
    try {
      // Get driver location (for production with real GPS)
      let location = { latitude: 0, longitude: 0 };
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            };
          }
        );
      }

      const data = await apiService.post('/scans/validate', { 
          driver_id: currentUser?.driver_id,
          passenger_qr: qrCodeText,
          location: location,
          device_fingerprint: navigator.userAgent
        }, { userType: 'driver' });

      if (data.valid && data.passenger) {
        // Extract passenger_id from QR code (format: subscription_id|passenger_id)
        const qrParts = qrCodeText.split('|');
        const passengerId = qrParts[1] || '';
        
        // Format response for display
        const planType = data.passenger.subscription || 'standard';
        const planInfo = PLAN_MAPPING[planType] || { name: planType.toUpperCase(), color: '#666' };
        
        setScanResult({
          valid: true,
          name: data.passenger.name,
          plan: planInfo.name,
          planType: planType,
          planColor: planInfo.color,
          expiryDate: new Date(data.passenger.valid_until).toLocaleDateString('fr-FR'),
          qrCode: qrCodeText,
          userId: passengerId,
          scanId: data.scan_id,
          status: 'active'
        });
      } else {
        setScanResult({
          valid: false,
          reason: data.message || 'QR code invalide ou abonnement expiré'
        });
      }
    } catch (error) {
      console.error('Error processing scan:', error);
      // Fallback for demo
      setScanResult({
        valid: true,
        name: 'Passager Identifié',
        plan: 'STANDARD',
        planType: 'standard',
        planColor: '#FFB900',
        expiryDate: '2026-02-24',
        qrCode: qrCodeText,
        status: 'active'
      });
    }
  };

  const acceptPassenger = async () => {
    if (!scanResult || !currentUser) {
      console.error('❌ Données manquantes:', { scanResult, currentUser });
      return;
    }

    if (!scanResult.userId) {
      console.error('❌ userId non trouvé dans scanResult:', scanResult);
      setScanResult(null);
      return;
    }

    try {
      console.log('📝 Création trajet avec:', {
        driver_id: currentUser.driver_id,
        passenger_id: scanResult.userId,
        scan_id: scanResult.scanId,
        route_id: activeRoute?.route_id || null,
        route_name: activeRoute?.name || null
      });

      // Create trip at backend with in_progress status
      const response = await apiService.post(
        '/trips',
        {
          driver_id: currentUser.driver_id,
          passenger_id: scanResult.userId,
          scan_id: scanResult.scanId,
          route_id: activeRoute?.route_id || null,
          route_name: activeRoute?.name || null,
          notes: '',
          create_scan: false  // Use the real scan_id from validation
        },
        { userType: 'driver' }
      );

      console.log('✅ Réponse création trajet:', response);

      if (response.success) {
        // Increment passenger counter
        setPassengerCount(passengerCount + 1);
        // Clear scan result to reset UI
        setScanResult(null);
      } else {
        console.error('❌ Erreur création trajet:', response.error);
        // Reset UI on error so user can retry
        setTimeout(() => {
          setScanResult(null);
        }, 2000);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'acceptation du passager:', error);
      // Reset UI on error so user can retry
      setTimeout(() => {
        setScanResult(null);
      }, 2000);
    }
  };

  // ==================== PAGES D'AUTHENTIFICATION ====================

  // Connexion sans mot de passe « numéro d'abord » (2026-10-30) — même parcours que l'app chauffeur :
  // numéro → code WhatsApp → chauffeur connu = accueil (ou attente de validation) ; compte sans profil
  // chauffeur ou numéro nouveau → « Devenir chauffeur » (RegisterPage en mode onboarding, sans mot de passe).
  const LoginPage = () => {
    const [error, setError] = useState('');
    const enterApp = (user) => {
      setCurrentUser(user);
      setIsAuthenticated(true);
      setIsDriverAvailable(user.is_available || false);
      if (user.status === 'pending') {
        setCurrentPage('pending');
      } else {
        setCurrentPage('home');
      }
    };
    const onSession = async (resp) => {
      setError('');
      const res = await authServiceDriver.persistOtpSession(resp);
      if (!res?.success) { setError(res?.error || 'Connexion impossible'); throw res; }
      if (res.needsOnboarding) {
        setOnboardUser(res.user || resp.user || null);
        localStorage.removeItem('driverRegisterStep');
        setCurrentPage('register');
        return;
      }
      enterApp(res.user);
    };

    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            {error && (
              <div className="error-message">
                <AlertCircle size={18} />
                {error}
              </div>
            )}
            <PhoneLoginWeb
              app="driver"
              accent="#7A3BB0"
              brand={<h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.4rem', marginBottom: '0.25rem' }}><img src="/app-logos/driver.png" alt="" style={{ width: 32, height: 32, borderRadius: 8 }} /> Mbiyo Chauffeur</h1>}
              onSession={onSession}
            />
          </div>
        </div>
      </div>
    );
  };

  const ForgotPasswordPage = () => {
    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState('+243 ');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailHint, setEmailHint] = useState('');
    const [devCode, setDevCode] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [channel, setChannel] = useState('email'); // V7 : 'whatsapp' | 'email'

    React.useEffect(() => {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      }
    }, [countdown]);

    const handlePhoneChange = (e) => {
      let value = e.target.value;
      if (!value.startsWith('+243')) value = '+243 ';
      value = value.replace(/[^\d+\s]/g, '');
      const numbers = value.replace(/[^\d]/g, '');
      if (numbers.length > 12) return;
      setPhone(value);
    };

    const handleRequestCode = async (e) => {
      e.preventDefault();
      const cleanPhone = phone.replace(/\s/g, '');
      if (cleanPhone.length < 13) { setError('Numéro de téléphone incomplet'); return; }
      setIsLoading(true); setError('');
      try {
        // V7 : WhatsApp d'abord, e-mail en secours
        const result = await authServiceDriver.forgotPassword(cleanPhone, { channel: 'auto', language: 'fr' });
        if (result.success) {
          setChannel(result.channel || 'email');
          setEmailHint(result.hint || result.emailHint || '');
          if (result.resetCode) setDevCode(result.resetCode);
          setStep(2); setCountdown(60);
          setSuccess(result.channel === 'whatsapp' ? 'Un code a été envoyé sur WhatsApp.' : 'Un code de vérification a été envoyé par e-mail.');
        } else { setError(result.error || 'Erreur lors de l\'envoi du code'); }
      } catch (err) { setError('Erreur: ' + err.message); }
      finally { setIsLoading(false); }
    };

    // V7 : wantedChannel 'email' → bascule vers l'e-mail à la demande
    const handleResendCode = async (wantedChannel) => {
      if (countdown > 0 && wantedChannel !== 'email') return;
      setIsLoading(true); setError('');
      try {
        const cleanPhone = phone.replace(/\s/g, '');
        const result = await authServiceDriver.forgotPassword(cleanPhone, { channel: wantedChannel === 'email' ? 'email' : 'auto', language: 'fr' });
        if (result.success) {
          setChannel(result.channel || 'email');
          setEmailHint(result.hint || result.emailHint || '');
          if (result.resetCode) setDevCode(result.resetCode);
          setCountdown(60); setSuccess(result.channel === 'whatsapp' ? 'Un nouveau code a été envoyé sur WhatsApp.' : 'Un nouveau code a été envoyé par e-mail.');
        } else { setError(result.error); }
      } catch (err) { setError('Erreur: ' + err.message); }
      finally { setIsLoading(false); }
    };

    const handleVerifyCode = async (e) => {
      e.preventDefault();
      if (resetCode.length !== 6) { setError('Le code doit contenir 6 chiffres'); return; }
      setIsLoading(true); setError('');
      try {
        const cleanPhone = phone.replace(/\s/g, '');
        const result = await authServiceDriver.verifyResetCode(cleanPhone, resetCode);
        if (result.success) { setStep(3); setSuccess('Code vérifié ! Choisissez votre nouveau mot de passe.'); }
        else { setError(result.error || 'Code incorrect'); }
      } catch (err) { setError('Erreur: ' + err.message); }
      finally { setIsLoading(false); }
    };

    const handleResetPassword = async (e) => {
      e.preventDefault();
      if (newPassword.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères'); return; }
      if (newPassword !== confirmPassword) { setError('Les mots de passe ne correspondent pas'); return; }
      setIsLoading(true); setError('');
      try {
        const cleanPhone = phone.replace(/\s/g, '');
        const result = await authServiceDriver.resetPassword(cleanPhone, resetCode, newPassword);
        if (result.success) {
          setSuccess('✅ Mot de passe réinitialisé avec succès !');
          setTimeout(() => { setCurrentPage('login'); }, 2000);
        } else { setError(result.error || 'Erreur lors de la réinitialisation'); }
      } catch (err) { setError('Erreur: ' + err.message); }
      finally { setIsLoading(false); }
    };

    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <button 
                onClick={() => step === 1 ? setCurrentPage('login') : setStep(step - 1)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center', color: '#F54119' }}
              >
                <ArrowLeft size={22} />
              </button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <h1 style={{ margin: 0, fontSize: '1.3rem' }}>🔒 Réinitialisation</h1>
              </div>
              <div style={{ width: '34px' }}></div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
              {[1, 2, 3].map(s => (
                <div key={s} style={{
                  width: s === step ? '2rem' : '0.75rem', height: '0.75rem',
                  borderRadius: '0.375rem', background: s <= step ? '#F54119' : '#E9EBEF',
                  transition: 'all 0.3s ease'
                }} />
              ))}
            </div>

            {error && (
              <div className="error-message"><AlertCircle size={18} />{error}</div>
            )}
            {success && !error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', marginBottom: '1rem', color: '#065f46', fontSize: '0.9rem' }}>
                <CheckCircle size={18} />{success}
              </div>
            )}

            {step === 1 && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ width: '60px', height: '60px', background: '#FEE9E2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                    <KeyRound size={28} color="#F54119" />
                  </div>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', margin: 0 }}>
                    Entrez votre numéro de téléphone. Un code de réinitialisation sera envoyé sur votre WhatsApp (ou par e-mail si vous n'avez pas WhatsApp).
                  </p>
                </div>
                <form onSubmit={handleRequestCode}>
                  <div className="form-group">
                    <label>Numéro de téléphone</label>
                    <div className="phone-input-wrapper">
                      <Phone size={20} className="phone-icon" />
                      <input type="tel" value={phone} onChange={handlePhoneChange} placeholder="+243 XXX XXX XXX" required />
                    </div>
                  </div>
                  <button type="submit" className="btn-auth" disabled={isLoading}>
                    {isLoading ? '⏳ Envoi en cours...' : '📨 Envoyer le code'}
                  </button>
                </form>
              </>
            )}

            {step === 2 && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ width: '60px', height: '60px', background: '#FEE9E2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                    <ShieldCheck size={28} color="#F54119" />
                  </div>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', margin: 0 }}>
                    {channel === 'whatsapp' ? 'Entrez le code à 6 chiffres envoyé sur WhatsApp au' : 'Entrez le code à 6 chiffres envoyé à votre adresse email'}
                    {emailHint && <><br/><strong style={{ color: '#F54119' }}>{emailHint}</strong></>}
                  </p>
                </div>
                {devCode && (
                  <div style={{ padding: '0.75rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center' }}>
                    <small style={{ color: '#92400e' }}>🧪 Mode dev — Code: <strong style={{ fontSize: '1.1rem', letterSpacing: '2px' }}>{devCode}</strong></small>
                  </div>
                )}
                <form onSubmit={handleVerifyCode}>
                  <div className="form-group">
                    <label>Code de vérification</label>
                    <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={resetCode}
                      onChange={(e) => { const val = e.target.value.replace(/[^0-9]/g, ''); if (val.length <= 6) setResetCode(val); }}
                      placeholder="• • • • • •" style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem', fontWeight: '600' }} required
                    />
                  </div>
                  <button type="submit" className="btn-auth" disabled={isLoading || resetCode.length !== 6}>
                    {isLoading ? '⏳ Vérification...' : '✅ Vérifier le code'}
                  </button>
                </form>
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  {countdown > 0 ? (
                    <p style={{ color: '#9AA0A8', fontSize: '0.85rem' }}>Renvoyer le code dans {countdown}s</p>
                  ) : (
                    <span onClick={() => handleResendCode()} style={{ color: '#F54119', cursor: 'pointer', fontSize: '0.9rem' }}>🔄 Renvoyer le code</span>
                  )}
                  {channel === 'whatsapp' && (
                    <p style={{ marginTop: '0.6rem' }}>
                      <span onClick={() => handleResendCode('email')} style={{ color: '#5C636E', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline' }}>
                        Je n'ai pas WhatsApp — recevoir le code par e-mail
                      </span>
                    </p>
                  )}
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ width: '60px', height: '60px', background: '#FEE9E2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                    <Lock size={28} color="#F54119" />
                  </div>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', margin: 0 }}>Choisissez votre nouveau mot de passe (minimum 8 caractères)</p>
                </div>
                <form onSubmit={handleResetPassword}>
                  <div className="form-group">
                    <label>Nouveau mot de passe</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" minLength={8} required />
                  </div>
                  <div className="form-group">
                    <label>Confirmer le mot de passe</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" minLength={8} required />
                  </div>
                  <button type="submit" className="btn-auth" disabled={isLoading}>
                    {isLoading ? '⏳ Réinitialisation...' : '🔐 Réinitialiser le mot de passe'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ==================== CAMERA MODAL ====================
  const CameraModal = ({ onCapture, onClose }) => {
    const videoRef = React.useRef(null);
    const canvasRef = React.useRef(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraError, setCameraError] = useState('');
    const streamRef = React.useRef(null);

    React.useEffect(() => {
      let timeoutId = null;

      const initCamera = async () => {
        try {
          setCameraError('');
          console.log('🎥 Demande d\'accès à la caméra...');
          
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('mediaDevices_not_available');
          }
          
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: 'user',
              width: { ideal: 640 },
              height: { ideal: 480 }
            },
            audio: false
          });
          
          console.log('✅ Caméra obtenue:', stream.getVideoTracks());
          streamRef.current = stream;
          
          if (!videoRef.current) return;
          
          videoRef.current.srcObject = stream;
          console.log('📹 Stream assigné à la vidéo');
          
          try {
            await videoRef.current.play();
            console.log('▶️ Video play() exécuté avec succès');
          } catch (err) {
            console.warn('⚠️ play() erreur (normal pour streaming):', err.message);
          }
          
          timeoutId = setTimeout(() => {
            console.log('⏱️ Activation timeout');
            setCameraActive(true);
          }, 500);
          
        } catch (err) {
          console.error('❌ Erreur caméra:', err);
          let errorMsg = '⚠️ Impossible d\'accéder à la caméra. ';
          
          if (err.name === 'NotAllowedError' || err.message === 'Permission denied') {
            errorMsg = '❌ Permission refusée. Autorise l\'accès à la caméra dans les paramètres du navigateur.';
          } else if (err.name === 'NotFoundError') {
            errorMsg = '❌ Aucune caméra trouvée. Vérifie que tu as une caméra connectée.';
          } else if (err.name === 'NotReadableError') {
            errorMsg = '❌ La caméra est déjà utilisée par une autre application.';
          } else if (err.name === 'TypeError' || err.message === 'mediaDevices_not_available') {
            errorMsg = '❌ Accès caméra non disponible. Cette fonction nécessite HTTPS ou localhost.';
          } else if (err.message && err.message.includes('Permission')) {
            errorMsg = '❌ Permission de caméra requise. Clique sur l\'icône de caméra dans la barre du navigateur.';
          } else {
            errorMsg += (err.message || err.name || 'Erreur inconnue');
          }
          setCameraError(errorMsg);
        }
      };
      
      initCamera();
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (streamRef.current) {
          console.log('🛑 Arrêt des tracks');
          streamRef.current.getTracks().forEach(track => {
            track.stop();
          });
          streamRef.current = null;
        }
      };
    }, []);

    const capturePhoto = () => {
      if (!videoRef.current || !canvasRef.current) {
        console.error('Canvas ou vidéo non disponible');
        return;
      }

      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        console.log(`📸 Capture: ${canvas.width}x${canvas.height}`);
        ctx.drawImage(video, 0, 0);

        const imgData = canvas.toDataURL('image/jpeg');
        const img = new Image();
        img.src = imgData;

        img.onload = () => {
          const maxWidth = 400;
          const maxHeight = 400;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round(height * maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round(width * maxHeight / height);
              height = maxHeight;
            }
          }

          const finalCanvas = document.createElement('canvas');
          finalCanvas.width = width;
          finalCanvas.height = height;
          const finalCtx = finalCanvas.getContext('2d');
          finalCtx.drawImage(img, 0, 0, width, height);

          const compressedPhoto = finalCanvas.toDataURL('image/jpeg', 0.8);
          
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          
          console.log('✅ Photo capturée');
          onCapture(compressedPhoto);
        };
      } catch (err) {
        console.error('❌ Erreur capture:', err);
        setCameraError('Erreur capture: ' + err.message);
      }
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <h3>Prendre une photo</h3>
            <button onClick={onClose} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer'}}>✕</button>
          </div>

          {cameraError && (
            <div style={{
              padding: '1rem',
              background: '#fee2e2',
              border: '1px solid #FEE2E2',
              borderRadius: '8px',
              color: '#991b1b',
              marginBottom: '1rem'
            }}>
              <p style={{margin: 0}}>⚠️ {cameraError}</p>
            </div>
          )}

          <div style={{
            position: 'relative',
            background: '#000',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '1rem',
            minHeight: '320px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
            {!cameraActive && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                textAlign: 'center'
              }}>
                <p style={{fontSize: '1.1rem'}}>🎥 Initialisation de la caméra...</p>
              </div>
            )}
          </div>

          <canvas ref={canvasRef} style={{display: 'none'}} />

          <div style={{display: 'flex', gap: '1rem'}}>
            <button
              onClick={capturePhoto}
              disabled={!cameraActive}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                background: cameraActive ? '#FF7800' : '#DCE0E6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: cameraActive ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                fontSize: '1rem'
              }}
            >
              📸 Capturer
            </button>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                background: '#E9EBEF',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem'
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RegisterPage = () => {
    // Mode « Devenir chauffeur » (2026-10-30) : compte utilisateur déjà connecté par code WhatsApp →
    // pas de téléphone ni de mot de passe à saisir, création du profil via /drivers/onboard.
    const onboard = !!onboardUser;
    // Charger depuis localStorage ou utiliser les valeurs par défaut
    const [step, setStep] = useState(() => {
      const saved = localStorage.getItem('driverRegisterStep');
      return saved ? parseInt(saved) : 1;
    });
    
    const [formData, setFormData] = useState(() => {
      const saved = localStorage.getItem('driverFormData');
      const base = saved ? JSON.parse(saved) : { name: '', phone: '+243 ', email: '', password: '', confirmPassword: '' };
      // Onboarding : nom et numéro du compte connecté (non modifiables ici)
      return onboardUser ? { ...base, name: onboardUser.name || base.name, phone: onboardUser.phone || base.phone, email: base.email || onboardUser.email || '' } : base;
    });
    
    const [vehicleData, setVehicleData] = useState(() => {
      const saved = localStorage.getItem('driverVehicleData');
      return saved ? JSON.parse(saved) : {
        brand: '',
        model: '',
        color: '',
        plate: '',
        year: ''
      };
    });
    
    const [routeData, setRouteData] = useState(() => {
      const saved = localStorage.getItem('driverRouteData');
      return saved ? JSON.parse(saved) : {
        primary_route: '',
        secondary_route: ''
      };
    });
    
    const [photo, setPhoto] = useState(() => {
      return localStorage.getItem('driverPhoto');
    });
    
    const [photoPreview, setPhotoPreview] = useState(() => {
      return localStorage.getItem('driverPhotoPreview');
    });
    
    const [idDocument, setIdDocument] = useState(() => {
      return localStorage.getItem('driverIdDocument');
    });
    
    const [idDocumentPreview, setIdDocumentPreview] = useState(() => {
      return localStorage.getItem('driverIdDocumentPreview');
    });
    
    const [error, setError] = useState('');
    const [routes, setRoutes] = useState([]);
    const [, setLoadingRoutes] = useState(false);
    const [showCameraModal, setShowCameraModal] = useState(false);
    const [cameraModalType, setCameraModalType] = useState(null); // 'photo', 'id', ou 'vehicle'
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [vehiclePhotos, setVehiclePhotos] = useState(() => {
      const saved = localStorage.getItem('driverVehiclePhotos');
      return saved ? JSON.parse(saved) : [];
    });
    
    const photoInputRef = React.useRef(null);
    const idDocInputRef = React.useRef(null);
    const vehiclePhotoInputRef = React.useRef(null);

    // Sauvegarder step dans localStorage
    React.useEffect(() => {
      localStorage.setItem('driverRegisterStep', step.toString());
    }, [step]);

    // Sauvegarder formData dans localStorage
    React.useEffect(() => {
      localStorage.setItem('driverFormData', JSON.stringify(formData));
    }, [formData]);

    // Sauvegarder vehicleData dans localStorage
    React.useEffect(() => {
      localStorage.setItem('driverVehicleData', JSON.stringify(vehicleData));
    }, [vehicleData]);

    // Sauvegarder routeData dans localStorage
    React.useEffect(() => {
      localStorage.setItem('driverRouteData', JSON.stringify(routeData));
    }, [routeData]);

    // Sauvegarder photo dans localStorage
    React.useEffect(() => {
      if (photo) {
        localStorage.setItem('driverPhoto', photo);
      }
    }, [photo]);

    // Sauvegarder photoPreview dans localStorage
    React.useEffect(() => {
      if (photoPreview) {
        localStorage.setItem('driverPhotoPreview', photoPreview);
      }
    }, [photoPreview]);

    // Sauvegarder idDocument dans localStorage
    React.useEffect(() => {
      if (idDocument) {
        localStorage.setItem('driverIdDocument', idDocument);
      }
    }, [idDocument]);

    // Sauvegarder idDocumentPreview dans localStorage
    React.useEffect(() => {
      if (idDocumentPreview) {
        localStorage.setItem('driverIdDocumentPreview', idDocumentPreview);
      }
    }, [idDocumentPreview]);

    // Sauvegarder vehiclePhotos dans localStorage
    React.useEffect(() => {
      localStorage.setItem('driverVehiclePhotos', JSON.stringify(vehiclePhotos));
    }, [vehiclePhotos]);

    // Charger les routes disponibles
    React.useEffect(() => {
      const loadRoutes = async () => {
        try {
          console.log('🚀 DÉBUT: Chargement des routes...');
          console.log('📍 API URL:', apiService.baseURL);
          console.log('🔧 REACT_APP_API_URL env:', process.env.REACT_APP_API_URL);
          setLoadingRoutes(true);
          const data = await apiService.request('/routes');
          console.log('✅ Données reçues:', data);
          if (data.success && data.routes) {
            console.log('📌 Nombre de routes:', data.routes.length);
            setRoutes(data.routes);
          } else {
            console.warn('⚠️ Réponse sans routes:', data);
          }
        } catch (error) {
          console.error('❌ Erreur chargement des routes:', error.message);
          console.error('Stack:', error.stack);
        } finally {
          setLoadingRoutes(false);
        }
      };
      loadRoutes();
    }, []);

    // Avertir si une tentative de quitter la page avec des données à remplir
    React.useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (step > 1 && formData.name) {
          e.preventDefault();
          e.returnValue = '';
          return '';
        }
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [step, formData]);

    const handlePhoneChange = (e) => {
      let value = e.target.value;
      if (!value.startsWith('+243')) {
        value = '+243 ';
      }
      value = value.replace(/[^\d+\s]/g, '');
      const numbers = value.replace(/[^\d]/g, '');
      if (numbers.length > 12) return;
      setFormData({ ...formData, phone: value });
    };

    const handlePhotoCapture = (e, type) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          setError('Veuillez sélectionner une image');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError('L\'image est trop volumineuse (max 5MB)');
          return;
        }

        // Compresser l'image
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.src = reader.result;
          img.onload = () => {
            // Créer un canvas pour redimensionner et compresser
            const canvas = document.createElement('canvas');
            const maxWidth = 400;
            const maxHeight = 400;
            let width = img.width;
            let height = img.height;

            // Redimensionner en gardant l'aspect ratio
            if (width > height) {
              if (width > maxWidth) {
                height = Math.round(height * maxWidth / width);
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = Math.round(width * maxHeight / height);
                height = maxHeight;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Convertir en JPEG avec compression (0.8 = 80% qualité)
            const compressedPhoto = canvas.toDataURL('image/jpeg', 0.8);
            
            if (type === 'photo') {
              setPhoto(compressedPhoto);
              setPhotoPreview(compressedPhoto);
            } else if (type === 'id') {
              setIdDocument(compressedPhoto);
              setIdDocumentPreview(compressedPhoto);
            }
            setError('');
          };
        };
        reader.readAsDataURL(file);
      }
    };

    const onPhotoCapturedFromCamera = (photoData) => {
      if (cameraModalType === 'photo') {
        setPhoto(photoData);
        setPhotoPreview(photoData);
      } else if (cameraModalType === 'id') {
        setIdDocument(photoData);
        setIdDocumentPreview(photoData);
      } else if (cameraModalType === 'vehicle') {
        if (vehiclePhotos.length < 5) {
          setVehiclePhotos(prev => [...prev, photoData]);
        }
      }
      setShowCameraModal(false);
      setCameraModalType(null);
      setError('');
    };

    const handleChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    };

    const handleVehicleChange = (e) => {
      setVehicleData({
        ...vehicleData,
        [e.target.name]: e.target.value
      });
    };

    const [otp, setOtp] = useState(null); // V7 : { phone, email, name, bot } → code WhatsApp avant la création
    const nextStep = () => {
      setError('');
      
      if (step === 1 && onboard) {
        if (formData.email && !formData.email.includes('@')) {
          setError('Email invalide');
          return;
        }
      }
      if (step === 1 && !onboard) {
        const cleanPhone = formData.phone.replace(/\s/g, '');
        if (cleanPhone.length < 13) {
          setError('Numéro de téléphone incomplet');
          return;
        }
        if (formData.password.length < 8) {
          setError('Le mot de passe doit contenir au moins 8 caractères');
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Les mots de passe ne correspondent pas');
          return;
        }
        if (formData.email && !formData.email.includes('@')) {
          setError('Email invalide');
          return;
        }
      }
      
      if (step === 2) {
        if (!photo) {
          setError('La photo de profil est obligatoire');
          return;
        }
        if (!idDocument) {
          setError('La photo de votre carte d\'identité ou permis est obligatoire');
          return;
        }
      }

      if (step === 3) {
        if (!vehicleData.brand || !vehicleData.model || !vehicleData.plate) {
          setError('Tous les champs du véhicule sont obligatoires');
          return;
        }
        if (vehicleData.plate.length < 5) {
          setError('Plaque d\'immatriculation invalide');
          return;
        }
      }

      if (step === 4) {
        // Vehicle photos are optional - no blocking validation
        // Just move to next step
      }
      
      setStep(step + 1);
    };

    const prevStep = () => {
      setError('');
      setStep(step - 1);
    };

    const submitOnboard = async () => {
      setError('');
      setIsSubmitting(true);
      try {
        const result = await authServiceDriver.onboard({
          email: formData.email || null,
          photo: photo,
          idDocument: idDocument,
          vehicle: vehicleData,
          vehiclePhotos: vehiclePhotos,
          primary_route: routeData.primary_route,
          secondary_route: routeData.secondary_route,
        });
        if (result.success) {
          ['driverRegisterStep', 'driverFormData', 'driverVehicleData', 'driverRouteData', 'driverPhoto', 'driverPhotoPreview', 'driverIdDocument', 'driverIdDocumentPreview', 'driverVehiclePhotos'].forEach((k) => localStorage.removeItem(k));
          setOnboardUser(null);
          setCurrentUser(result.user);
          setIsAuthenticated(true);
          setIsDriverAvailable(result.user?.is_available || false);
          setCurrentPage(result.status === 'approved' ? 'home' : 'pending');
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Erreur lors de l\'inscription: ' + err.message);
      } finally {
        setIsSubmitting(false);
      }
    };
    const handleRegister = async (e) => {
      e.preventDefault();
      setError('');
      if (onboard) { await submitOnboard(); return; }
      // V7 : le numéro est confirmé par un code WhatsApp AVANT la création du compte
      // (les champs BotShield partent avec la demande de code ; le jeton de vérification dispense
      // ensuite l'inscription du captcha, à usage unique)
      const cleanPhone = formData.phone.replace(/\s/g, '');
      setOtp({ phone: cleanPhone, email: formData.email || undefined, name: formData.name, bot: botFields('driver-register') });
    };
    const submitRegistration = async (verificationToken) => {
      setOtp(null);
      setError('');
      setIsSubmitting(true);

      const cleanPhone = formData.phone.replace(/\s/g, '');

      try {
        const result = await authServiceDriver.register({
          name: formData.name,
          phone: cleanPhone,
          email: formData.email || null,
          password: formData.password,
          photo: photo,
          idDocument: idDocument,
          vehicle: vehicleData,
          vehiclePhotos: vehiclePhotos,
          primary_route: routeData.primary_route,
          secondary_route: routeData.secondary_route,
          phone_verification_token: verificationToken,
          bot: botFields('driver-register'),
        });
        resetBotShield('driver-register');

        if (result.success) {
          // Nettoyer localStorage après inscription réussie
          localStorage.removeItem('driverRegisterStep');
          localStorage.removeItem('driverFormData');
          localStorage.removeItem('driverVehicleData');
          localStorage.removeItem('driverRouteData');
          localStorage.removeItem('driverPhoto');
          localStorage.removeItem('driverPhotoPreview');
          localStorage.removeItem('driverIdDocument');
          localStorage.removeItem('driverIdDocumentPreview');
          localStorage.removeItem('driverVehiclePhotos');
          
          setCurrentUser(result.user);
          setIsAuthenticated(true);
          setCurrentPage('pending');
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Erreur lors de l\'inscription: ' + err.message);
      } finally {
        setIsSubmitting(false);
      }
    };

    const resetForm = () => {
      if (window.confirm('Êtes-vous sûr de vouloir recommencer ? Toutes vos données seront effacées.')) {
        // Réinitialiser les états
        setStep(1);
        setFormData({
          name: '',
          phone: '+243 ',
          email: '',
          password: '',
          confirmPassword: ''
        });
        setVehicleData({
          brand: '',
          model: '',
          color: '',
          plate: '',
          year: ''
        });
        setRouteData({
          primary_route: '',
          secondary_route: ''
        });
        setPhoto(null);
        setPhotoPreview(null);
        setIdDocument(null);
        setIdDocumentPreview(null);
        setVehiclePhotos([]);
        setError('');
        
        // Nettoyer localStorage
        localStorage.removeItem('driverRegisterStep');
        localStorage.removeItem('driverFormData');
        localStorage.removeItem('driverVehicleData');
        localStorage.removeItem('driverRouteData');
        localStorage.removeItem('driverPhoto');
        localStorage.removeItem('driverPhotoPreview');
        localStorage.removeItem('driverIdDocument');
        localStorage.removeItem('driverIdDocumentPreview');
        localStorage.removeItem('driverVehiclePhotos');
      }
    };

    return (
      <div className="auth-page">
        <div className="auth-container driver-register">
          <PhoneVerifyModal open={!!otp} phone={otp?.phone} purpose="register" account="driver" email={otp?.email} name={otp?.name} bot={otp?.bot}
            onVerified={submitRegistration} onClose={() => { setOtp(null); resetBotShield('driver-register'); }} />
          <div className="auth-card">
            <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}><img src="/app-logos/driver.png" alt="" style={{ width: 36, height: 36, borderRadius: 9 }} /> Mbiyo Chauffeur</h1>
            <h2>Devenir chauffeur</h2>
            
            <div className="steps-indicator">
              <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
              <div className="step-line"></div>
              <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
              <div className="step-line"></div>
              <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
              <div className="step-line"></div>
              <div className={`step ${step >= 4 ? 'active' : ''}`}>4</div>
              <div className="step-line"></div>
              <div className={`step ${step >= 5 ? 'active' : ''}`}>5</div>
            </div>
            <p className="auth-subtitle">
              {step === 1 && 'Étape 1: Informations personnelles'}
              {step === 2 && 'Étape 2: Documents'}
              {step === 3 && 'Étape 3: Véhicule'}
              {step === 4 && 'Étape 4: Photos du véhicule'}
              {step === 5 && 'Étape 5: Lignes de travail'}
            </p>

            {error && (
              <div className="error-message">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <form onSubmit={step === 5 ? handleRegister : (e) => { e.preventDefault(); nextStep(); }}>
              
              {step === 1 && onboard && (
                <>
                  <div style={{ background: '#F4F1F8', border: '1px solid #DCD6E4', borderRadius: 12, padding: '0.9rem 1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#5C636E' }}>
                    Compte <b style={{ color: '#1C1C1C' }}>{formData.name}</b> · <b style={{ color: '#1C1C1C' }}>{formData.phone}</b> — numéro confirmé par code WhatsApp. Ensuite : votre photo, vos documents et votre véhicule.
                  </div>
                  <div className="form-group">
                    <label>Email <span className="optional">(facultatif)</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                    />
                  </div>
                </>
              )}

              {step === 1 && !onboard && (
                <>
                  <div className="form-group">
                    <label>Nom complet <span className="required">*</span></label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jean Mukendi"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Numéro de téléphone <span className="required">*</span></label>
                    <div className="phone-input-wrapper">
                      <Phone size={20} className="phone-icon" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="+243 XXX XXX XXX"
                        required
                      />
                    </div>
                    <small className="input-hint">Format: +243 suivi de 9 chiffres</small>
                  </div>

                  <div className="form-group">
                    <label>Email <span className="optional">(facultatif)</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Mot de passe <span className="required">*</span></label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Au moins 8 caractères"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirmer le mot de passe <span className="required">*</span></label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Répétez le mot de passe"
                      required
                    />
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="form-group">
                    <label>Photo de profil <span className="required">*</span></label>
                    <div className="photo-capture-area">
                      {photoPreview ? (
                        <div className="photo-preview">
                          <img src={photoPreview} alt="Profil" />
                          <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem'}}>
                            <button 
                              type="button" 
                              className="change-photo-btn"
                              onClick={() => {
                                setCameraModalType('photo');
                                setShowCameraModal(true);
                              }}
                              style={{flex: 1}}
                            >
                              📷 Caméra
                            </button>
                            <button 
                              type="button" 
                              className="change-photo-btn"
                              onClick={() => photoInputRef.current.click()}
                              style={{flex: 1}}
                            >
                              📁 Importer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2rem 1rem',
                          border: '2px dashed #DCE0E6',
                          borderRadius: '12px',
                          textAlign: 'center'
                        }}>
                          <Camera size={48} style={{color: '#9AA0A8', marginBottom: '1rem'}} />
                          <h4 style={{margin: '0 0 0.5rem 0', color: '#414852', fontSize: '1.1rem', fontWeight: '600'}}>
                            Choisissez une option
                          </h4>
                          <p style={{margin: '0 0 1.5rem 0', color: '#9AA0A8', fontSize: '0.9rem'}}>
                            Obligatoire
                          </p>
                          <div style={{display: 'flex', gap: '1rem', width: '100%'}}>
                            <button 
                              type="button" 
                              onClick={() => {
                                setCameraModalType('photo');
                                setShowCameraModal(true);
                              }}
                              style={{
                                flex: 1,
                                padding: '1rem',
                                background: '#FF7800',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#F54119'}
                              onMouseOut={(e) => e.target.style.background = '#FF7800'}
                            >
                              <Camera size={20} /> Prendre
                            </button>
                            <button 
                              type="button" 
                              onClick={() => photoInputRef.current.click()}
                              style={{
                                flex: 1,
                                padding: '1rem',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#059669'}
                              onMouseOut={(e) => e.target.style.background = '#10b981'}
                            >
                              <Upload size={20} /> Importer
                            </button>
                          </div>
                        </div>
                      )}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoCapture(e, 'photo')}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Carte d'identité ou Permis de conduire <span className="required">*</span></label>
                    <div className="photo-capture-area">
                      {idDocumentPreview ? (
                        <div className="document-preview">
                          <img src={idDocumentPreview} alt="Document" />
                          <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem'}}>
                            <button 
                              type="button" 
                              className="change-photo-btn"
                              onClick={() => {
                                setCameraModalType('id');
                                setShowCameraModal(true);
                              }}
                              style={{flex: 1}}
                            >
                              📷 Caméra
                            </button>
                            <button 
                              type="button" 
                              className="change-photo-btn"
                              onClick={() => idDocInputRef.current.click()}
                              style={{flex: 1}}
                            >
                              📁 Importer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2rem 1rem',
                          border: '2px dashed #DCE0E6',
                          borderRadius: '12px',
                          textAlign: 'center'
                        }}>
                          <Camera size={48} style={{color: '#9AA0A8', marginBottom: '1rem'}} />
                          <h4 style={{margin: '0 0 0.5rem 0', color: '#414852', fontSize: '1.1rem', fontWeight: '600'}}>
                            Choisissez une option
                          </h4>
                          <p style={{margin: '0 0 1.5rem 0', color: '#9AA0A8', fontSize: '0.9rem'}}>
                            Obligatoire
                          </p>
                          <div style={{display: 'flex', gap: '1rem', width: '100%'}}>
                            <button 
                              type="button" 
                              onClick={() => {
                                setCameraModalType('id');
                                setShowCameraModal(true);
                              }}
                              style={{
                                flex: 1,
                                padding: '1rem',
                                background: '#FF7800',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#F54119'}
                              onMouseOut={(e) => e.target.style.background = '#FF7800'}
                            >
                              <Camera size={20} /> Prendre
                            </button>
                            <button 
                              type="button" 
                              onClick={() => idDocInputRef.current.click()}
                              style={{
                                flex: 1,
                                padding: '1rem',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#059669'}
                              onMouseOut={(e) => e.target.style.background = '#10b981'}
                            >
                              <Upload size={20} /> Importer
                            </button>
                          </div>
                        </div>
                      )}
                      <input
                        ref={idDocInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoCapture(e, 'id')}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="form-group">
                    <label>Marque du véhicule <span className="required">*</span></label>
                    <input
                      type="text"
                      name="brand"
                      value={vehicleData.brand}
                      onChange={handleVehicleChange}
                      placeholder="Ex: Toyota, Mercedes, Nissan"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Modèle <span className="required">*</span></label>
                    <input
                      type="text"
                      name="model"
                      value={vehicleData.model}
                      onChange={handleVehicleChange}
                      placeholder="Ex: Hiace, Sprinter, Caravan"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Couleur <span className="required">*</span></label>
                    <input
                      type="text"
                      name="color"
                      value={vehicleData.color}
                      onChange={handleVehicleChange}
                      placeholder="Ex: Blanc, Noir, Bleu"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Plaque d'immatriculation <span className="required">*</span></label>
                    <div className="phone-input-wrapper">
                      <Car size={20} className="phone-icon" />
                      <input
                        type="text"
                        name="plate"
                        value={vehicleData.plate}
                        onChange={handleVehicleChange}
                        placeholder="Ex: AB 123 CD"
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                    <small className="input-hint">Plaque complète du véhicule</small>
                  </div>

                  <div className="form-group">
                    <label>Année <span className="optional">(facultatif)</span></label>
                    <input
                      type="number"
                      name="year"
                      value={vehicleData.year}
                      onChange={handleVehicleChange}
                      placeholder="Ex: 2020"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ color: '#5C636E', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Ajoutez entre <strong>3 et 5 photos</strong> de votre véhicule (extérieur, intérieur, plaque...)
                    </p>
                    <p style={{ fontWeight: '600', color: vehiclePhotos.length >= 3 ? '#10b981' : '#FFB900', fontSize: '0.95rem' }}>
                      {vehiclePhotos.length}/5 photo{vehiclePhotos.length > 1 ? 's' : ''} ajoutée{vehiclePhotos.length > 1 ? 's' : ''}
                      {vehiclePhotos.length < 3 && ` — minimum ${3 - vehiclePhotos.length} de plus recommandé`}
                    </p>
                    <p style={{ color: '#9AA0A8', fontSize: '0.8rem', marginTop: '0.25rem', fontStyle: 'italic' }}>
                      Vous pourrez aussi les ajouter plus tard depuis votre profil.
                    </p>
                  </div>

                  {/* Grille des photos ajoutées */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1.5rem'
                  }}>
                    {vehiclePhotos.map((vPhoto, idx) => (
                      <div key={idx} style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', aspectRatio: '4/3', border: '2px solid #E9EBEF' }}>
                        <img src={vPhoto} alt={`Véhicule ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          type="button"
                          onClick={() => setVehiclePhotos(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            position: 'absolute', top: '4px', right: '4px',
                            background: 'rgba(239,68,68,0.9)', color: 'white', border: 'none',
                            borderRadius: '50%', width: '28px', height: '28px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1rem', fontWeight: '700', lineHeight: 1
                          }}
                        >
                          ✕
                        </button>
                        <div style={{
                          position: 'absolute', bottom: '0', left: '0', right: '0',
                          background: 'rgba(0,0,0,0.5)', color: 'white', textAlign: 'center',
                          padding: '2px', fontSize: '0.75rem'
                        }}>
                          Photo {idx + 1}
                        </div>
                      </div>
                    ))}

                    {/* Bouton ajouter */}
                    {vehiclePhotos.length < 5 && (
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        border: '2px dashed #DCE0E6', borderRadius: '12px', aspectRatio: '4/3',
                        cursor: 'pointer', background: '#FFFFFF'
                      }}>
                        <Camera size={28} style={{ color: '#9AA0A8', marginBottom: '0.5rem' }} />
                        <span style={{ fontSize: '0.8rem', color: '#5C636E', fontWeight: '500' }}>Ajouter</span>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => { setCameraModalType('vehicle'); setShowCameraModal(true); }}
                            style={{
                              padding: '0.4rem 0.6rem', background: '#FF7800', color: 'white',
                              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600'
                            }}
                          >
                            📷
                          </button>
                          <button
                            type="button"
                            onClick={() => vehiclePhotoInputRef.current.click()}
                            style={{
                              padding: '0.4rem 0.6rem', background: '#10b981', color: 'white',
                              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600'
                            }}
                          >
                            📁
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <input
                    ref={vehiclePhotoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const remaining = 5 - vehiclePhotos.length;
                      const toProcess = files.slice(0, remaining);
                      toProcess.forEach(file => {
                        if (!file.type.startsWith('image/')) return;
                        if (file.size > 5 * 1024 * 1024) return;
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const img = new Image();
                          img.src = reader.result;
                          img.onload = () => {
                            const canvas = document.createElement('canvas');
                            const maxW = 800, maxH = 600;
                            let w = img.width, h = img.height;
                            if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
                            if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
                            canvas.width = w; canvas.height = h;
                            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                            const compressed = canvas.toDataURL('image/jpeg', 0.75);
                            setVehiclePhotos(prev => prev.length < 5 ? [...prev, compressed] : prev);
                          };
                        };
                        reader.readAsDataURL(file);
                      });
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                </>
              )}

              {step === 5 && (
                <>
                  <div className="form-group">
                    <label>Ligne habituelle <span className="required">*</span></label>
                    <select
                      value={routeData.primary_route}
                      onChange={(e) => setRouteData({...routeData, primary_route: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '0.9rem 1rem',
                        border: '2px solid #E9EBEF',
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontFamily: '\'Poppins\', sans-serif',
                        cursor: 'pointer'
                      }}
                      required
                    >
                      <option value="">-- Sélectionnez votre ligne habituelle --</option>
                      {routes.filter(r => r.active).map(route => (
                        <option key={route.route_id} value={route.route_id}>
                          {route.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Ligne secondaire <span className="optional">(facultatif)</span></label>
                    <select
                      value={routeData.secondary_route}
                      onChange={(e) => setRouteData({...routeData, secondary_route: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '0.9rem 1rem',
                        border: '2px solid #E9EBEF',
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontFamily: '\'Poppins\', sans-serif',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">-- Aucune ligne secondaire --</option>
                      {routes.filter(r => r.active).map(route => (
                        <option key={route.route_id} value={route.route_id}>
                          {route.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="form-actions">
                {step > 1 && (
                  <button type="button" className="btn-secondary" onClick={prevStep}>
                    Précédent
                  </button>
                )}
                {step === 1 && formData.name && (
                  <button type="button" className="btn-secondary" onClick={resetForm} style={{ marginRight: 'auto' }} disabled={isSubmitting}>
                    🔄 Recommencer
                  </button>
                )}
                <button type="submit" className="btn-auth" disabled={isSubmitting}>
                  {isSubmitting ? '⏳ Inscription en cours...' : (step === 5 ? 'Terminer l\'inscription' : 'Suivant')}
                </button>
              </div>
              {step === 5 && <BotShield formKey="driver-register" baseURL={apiService.baseURL} />}
              {step === 5 && (
                <p style={{ fontSize: '.85rem', color: '#6b7280', marginTop: 12, lineHeight: 1.5 }}>
                  En terminant l'inscription, vous acceptez les{' '}
                  <a href="/cgu" target="_blank" rel="noopener noreferrer">Conditions générales d'utilisation</a> et la{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Politique de confidentialité</a> de Mbiyo.
                </p>
              )}
            </form>

            {step === 1 && (
              <p className="auth-switch">
                Déjà un compte ?{' '}
                <span onClick={() => setCurrentPage('login')}>
                  Se connecter
                </span>
              </p>
            )}
          </div>

          {showCameraModal && (
            <Stable render={CameraModal} key="CameraModal" 
              onCapture={onPhotoCapturedFromCamera}
              onClose={() => {
                setShowCameraModal(false);
                setCameraModalType(null);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const PendingPage = () => (
    <div className="auth-page">
      <div className="auth-container">
        <div className="pending-approval">
          <h3>⏳ Compte en attente</h3>
          <p>
            Votre inscription a bien été reçue !<br />
            Notre équipe examine votre dossier et validera votre compte sous 24-48h.
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
            Vous recevrez une notification une fois votre compte approuvé.
          </p>
          <button 
            className="btn-auth" 
            style={{ marginTop: '1.5rem' }}
            onClick={() => {
              authServiceDriver.logout();
              setIsAuthenticated(false);
              setCurrentUser(null);
              setCurrentPage('login');
            }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );

  // ==================== PAGES PRINCIPALES ====================

  const HomePage = () => {
    // Si le chauffeur est bloqué, afficher l'écran de blocage
    if (isDriverBlocked) {
      return (
        <div style={{ padding: '2rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <Lock size={40} style={{ color: '#dc2626' }} />
          </div>
          <h2 style={{ fontSize: '1.4rem', color: '#dc2626', marginBottom: '0.5rem' }}>Compte bloqué</h2>
          <p style={{ color: '#5C636E', fontSize: '0.95rem', marginBottom: '0.5rem', maxWidth: '320px' }}>
            Votre compte est bloqué en raison d'une dette impayée de
          </p>
          <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#dc2626', marginBottom: '1rem' }}>
            {driverBlockedDebt.toLocaleString('fr-FR')} CDF
          </div>
          <p style={{ color: '#9AA0A8', fontSize: '0.85rem', marginBottom: '1.5rem', maxWidth: '320px' }}>
            Vous ne pouvez plus accepter de courses. Veuillez contacter l'administration pour régulariser votre situation.
          </p>
          <a
            href="tel:+243000000000"
            style={{ padding: '14px 32px', background: '#dc2626', color: 'white', borderRadius: '14px', fontWeight: '600', textDecoration: 'none', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Phone size={20} /> Contacter l'admin
          </a>
          <button
            onClick={() => setCurrentPage('my-balance')}
            style={{ marginTop: '1rem', padding: '12px 28px', background: '#F4F5F7', color: '#414852', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
          >
            Voir mon solde
          </button>
        </div>
      );
    }

    return (
    <div className="home-page">
      <div className="driver-card">
        <div className="driver-info">
          <div className="driver-avatar">{driver.name.charAt(0)}</div>
          <div>
            <h2>{driver.name}</h2>
            <p className="vehicle-info">{driver.vehicle}</p>
            <div className="rating">
              {driver.rating_count < 5 ? '⭐ Nouveau' : `${'⭐'.repeat(Math.floor(driver.rating))} ${Number(driver.rating).toFixed(1)}`}
            </div>
          </div>
        </div>
      </div>

      {/* ===== TOGGLE DISPONIBILITÉ ON/OFF ===== */}
      <div
        onClick={() => toggleAvailability()}
        style={{
          background: isDriverAvailable
            ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)'
            : 'linear-gradient(135deg, #F4F5F7, #E9EBEF)',
          border: `2px solid ${isDriverAvailable ? '#22c55e' : '#DCE0E6'}`,
          borderRadius: '16px',
          padding: '1rem 1.25rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: togglingAvailability ? 'wait' : 'pointer',
          transition: 'all 0.3s ease',
          opacity: togglingAvailability ? 0.7 : 1
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%',
            background: isDriverAvailable ? '#22c55e' : '#9AA0A8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.3s ease',
            boxShadow: isDriverAvailable ? '0 0 12px rgba(34,197,94,0.4)' : 'none'
          }}>
            <Car size={24} color="white" />
          </div>
          <div>
            <p style={{ fontWeight: '700', fontSize: '1.05rem', margin: 0, color: isDriverAvailable ? '#15803d' : '#5C636E' }}>
              {isDriverAvailable ? t.driverOnline : t.driverOffline}
            </p>
            <p style={{ fontSize: '0.82rem', margin: '2px 0 0', color: isDriverAvailable ? '#16a34a' : '#9AA0A8' }}>
              {isDriverAvailable ? t.goOffline : t.goOnline}
            </p>
          </div>
        </div>
        {/* Toggle switch visuel */}
        <div style={{
          width: '56px', height: '30px', borderRadius: '15px',
          background: isDriverAvailable ? '#22c55e' : '#DCE0E6',
          position: 'relative', transition: 'background 0.3s ease',
          flexShrink: 0
        }}>
          <div style={{
            width: '26px', height: '26px', borderRadius: '50%',
            background: 'white',
            position: 'absolute', top: '2px',
            left: isDriverAvailable ? '28px' : '2px',
            transition: 'left 0.3s ease',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
          }} />
        </div>
      </div>

      {/* ===== MODE SWITCHER : Chauffeur / Livreur ===== */}
      {/* Visible uniquement si le chauffeur a les deux rôles approuvés */}
      {(() => {
        const roles = currentUser?.approved_roles || [];
        const hasBoth = roles.includes('driver') && roles.includes('deliverer');
        if (!hasBoth) return null;
        return (
          <div style={{
            display: 'flex', background: '#E9EBEF', borderRadius: '14px',
            padding: '4px', marginBottom: '1rem', gap: '4px',
          }}>
            <button
              onClick={() => switchMode('driver')}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '6px', padding: '11px', borderRadius: '11px', border: 'none',
                background: activeMode === 'driver' ? '#F54119' : 'transparent',
                color: activeMode === 'driver' ? 'white' : '#5C636E',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Car size={16} />
              Chauffeur
            </button>
            <button
              onClick={() => switchMode('deliverer')}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '6px', padding: '11px', borderRadius: '11px', border: 'none',
                background: activeMode === 'deliverer' ? '#059669' : 'transparent',
                color: activeMode === 'deliverer' ? 'white' : '#5C636E',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              📦 Livreur
            </button>
          </div>
        );
      })()}

      {/* Banner: compléter le profil si photos véhicule manquantes */}
      {(!currentUser?.vehicle?.photos || currentUser.vehicle.photos.length < 3) && (
        <div 
          onClick={() => setCurrentPage('profile')}
          style={{
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1px solid #FFB900',
            borderRadius: '14px', padding: '1rem 1.25rem', marginBottom: '1rem',
            display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer'
          }}
        >
          <Camera size={28} style={{ color: '#FF9B00', flexShrink: 0 }} />
          <div>
            <p style={{ fontWeight: '600', color: '#92400e', fontSize: '0.95rem', margin: 0 }}>
              Complétez votre profil
            </p>
            <p style={{ color: '#a16207', fontSize: '0.82rem', margin: '0.15rem 0 0' }}>
              Ajoutez les photos de votre véhicule pour finaliser votre dossier
            </p>
          </div>
          <ArrowLeft size={18} style={{ color: '#FF9B00', transform: 'rotate(180deg)', flexShrink: 0 }} />
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                onClick={() => setScanFilter('today')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: scanFilter === 'today' ? '#F54119' : '#E9EBEF',
                  color: scanFilter === 'today' ? 'white' : '#5C636E',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                {t.today}
              </button>
              <button
                onClick={() => setScanFilter('week')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: scanFilter === 'week' ? '#F54119' : '#E9EBEF',
                  color: scanFilter === 'week' ? 'white' : '#5C636E',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                {t.thisWeek}
              </button>
              <button
                onClick={() => setScanFilter('month')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: scanFilter === 'month' ? '#F54119' : '#E9EBEF',
                  color: scanFilter === 'month' ? 'white' : '#5C636E',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                {t.thisMonth}
              </button>
            </div>
          </div>
          <div className="stat-icon scans">
            <Camera size={28} />
          </div>
          <div>
            <p className="stat-value">
              {scanFilter === 'today' ? scanStats.today : scanFilter === 'week' ? scanStats.week : scanStats.month}
            </p>
            <p className="stat-label">
              {scanFilter === 'today' ? t.scansToday : scanFilter === 'week' ? t.scansThisWeek : t.scansThisMonth}
            </p>
          </div>
        </div>

        <div className="stat-card" onClick={() => setCurrentPage('currentPassengers')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon passengers">
            <Users size={24} />
          </div>
          <div>
            <p className="stat-value">{passengerCount}</p>
            <p className="stat-label">{t.passengersOnBoard}</p>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <button className="action-btn primary" onClick={() => setCurrentPage('scanner')}>
          <Camera size={24} />
          {t.scanQR}
        </button>
        <button className="action-btn secondary" onClick={() => setCurrentPage('route')}>
          <MapPin size={24} />
          {t.myRoute}
        </button>
      </div>

      {activeRoute && (
        <div className="active-route-card">
          <h3>{t.routeActive}</h3>
          <p className="route-name">{activeRoute.name}</p>
          {activeRoute.departure && activeRoute.arrival && (
            <div className="route-info">
              <MapPin size={16} />
              <span>{activeRoute.departure.name} → {activeRoute.arrival.name}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
  };

  const backButtonStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF9B00', cursor: 'pointer', fontSize: '0.95rem', padding: '0.5rem 0', marginBottom: '0.75rem', fontFamily: 'inherit', fontWeight: '500' };

  const BackButton = () => (
    <button onClick={() => setCurrentPage('home')} style={backButtonStyle}>
      <ArrowLeft size={20} />
      Retour
    </button>
  );

  const ScannerPage = () => (
    <div className="scanner-page">
      <Stable render={BackButton} key="BackButton" />
      <h2>{t.scanPassenger}</h2>
      
      {!scanResult ? (
        <div className="scanner-container">
          {!isScanning && !cameraError && (
            <div className="scanner-init">
              <Camera size={48} />
              <p>Cliquez pour activer la caméra et scanner un QR code</p>
              <button 
                className="scan-btn primary"
                onClick={initializeScanner}
              >
                Démarrer Scanner 📱
              </button>
            </div>
          )}

          {cameraError && (
            <div className="camera-error">
              <AlertTriangle size={48} color="#dc2626" />
              <p className="error-title">Erreur caméra</p>
              <p className="error-message">{cameraError}</p>
              <div className="troubleshooting">
                <h4>Vérifiez:</h4>
                <ul>
                  <li>✔️ La caméra est connectée</li>
                  <li>✔️ Les permissions sont accordées au navigateur</li>
                  <li>✔️ Utilisez un dispositif compatible</li>
                  <li>✔️ Essayez un autre navigateur</li>
                </ul>
              </div>
              <button 
                className="scan-btn primary"
                onClick={initializeScanner}
              >
                Réessayer
              </button>
            </div>
          )}

          {isScanning && (
            <div className="scanning-active">
              <div id="qr-reader" style={{width: '100%', maxWidth: '500px'}}></div>
              <div className="scanner-status">
                <Loader size={24} className="spinner" />
                <p>Alignez le QR code dans le cadre...</p>
              </div>
              <button 
                className="scan-btn secondary"
                onClick={stopScanner}
              >
                Arrêter Scanner
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="scan-result">
          <div className={`result-card ${scanResult.valid ? 'valid' : 'invalid'}`}>
            <div className="result-icon">
              {scanResult.valid ? <CheckCircle size={48} /> : <XCircle size={48} />}
            </div>
            <h3>{scanResult.valid ? t.validSubscription : t.invalidSubscription}</h3>
            
            {scanResult.valid && (
              <>
                <div className="passenger-info">
                  <p className="passenger-name">{scanResult.name}</p>
                  <div className="subscription-badge" style={{background: scanResult.planColor || '#FFB900', color: 'white'}}>
                    {scanResult.plan}
                  </div>
                  <p className="expiry-date">
                    {t.expiryDate}: {scanResult.expiryDate}
                  </p>
                  <p className="qr-code">QR: {scanResult.qrCode.substring(0, 30)}...</p>
                </div>
                
                <div className="action-buttons">
                  <button className="btn-accept" style={{background: scanResult.planColor || '#10b981'}} onClick={acceptPassenger}>
                    <CheckCircle size={20} />
                    {t.acceptPassenger}
                  </button>
                  <button className="btn-refuse" onClick={() => setScanResult(null)}>
                    <XCircle size={20} />
                    {t.refusePassenger}
                  </button>
                </div>
              </>
            )}

            {!scanResult.valid && (
              <p className="error-reason">{scanResult.reason || 'Abonnement invalide ou expiré'}</p>
            )}
            
            <button className="btn-scan-another" onClick={() => { setScanResult(null); initializeScanner(); }}>
              {t.scanAnother}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const RoutePage = () => (
    <div className="route-page">
      <Stable render={BackButton} key="BackButton" />
      <h2>{t.myRoute}</h2>
      
      {!activeRoute ? (
        <div className="route-selection">
          <p className="route-subtitle">{t.selectRoute}</p>
          {loadingDriverRoutes ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Loader size={32} className="spinning" />
              <p>Chargement des lignes...</p>
            </div>
          ) : driverRoutes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#5C636E' }}>
              <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>Aucune ligne disponible</p>
            </div>
          ) : (
            <div className="routes-list">
              {driverRoutes.map(route => (
                <div 
                  key={route.route_id}
                  className="route-card"
                  onClick={() => setActiveRoute(route)}
                >
                  <MapPin size={24} />
                  <div className="route-details">
                    <h3>{route.name}</h3>
                    <div className="route-meta">
                      {route.departure && <span>{route.departure.name}</span>}
                      <span>→</span>
                      {route.arrival && <span>{route.arrival.name}</span>}
                      {route.bidirectional && (
                        <span style={{ fontSize: '11px', color: '#5C636E', marginLeft: '6px' }}>↔</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="active-route">
          <div className="route-header">
            <h3>{activeRoute.name}</h3>
            <button className="btn-end-route" onClick={() => setActiveRoute(null)}>
              {t.endRoute}
            </button>
          </div>
          
          <div className="route-stats">
            <div className="stat">
              <Users size={20} />
              <span>{passengerCount} / {currentUser?.vehicle?.seats || 14}</span>
            </div>
          </div>

          <div className="seats-grid">
            {[...Array(currentUser?.vehicle?.seats || 14)].map((_, i) => (
              <div 
                key={i} 
                className={`seat ${i < passengerCount ? 'occupied' : 'available'}`}
              >
                {i + 1}
              </div>
            ))}
          </div>

          <p className="seats-info">
            {t.availableSeats}: {(currentUser?.vehicle?.seats || 14) - passengerCount} / {t.totalSeats}: {currentUser?.vehicle?.seats || 14}
          </p>
        </div>
      )}
    </div>
  );

  const CurrentPassengersPage = () => (
    <div className="current-passengers-page">
      <Stable render={BackButton} key="BackButton" />
      <h2 style={{ marginBottom: '20px', color: '#1C1C1C', fontSize: '22px', fontWeight: '600' }}>
        {t.currentPassengersTitle}
      </h2>
      {currentUser && currentUser.driver_id && (
        <CurrentPassengers driverId={currentUser.driver_id} language={language} />
      )}
    </div>
  );

  // ==================== PAGE PROFIL ====================
  const ProfilePage = () => {
    const [profileData, setProfileData] = useState({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
    });
    const [profileVehicle, setProfileVehicle] = useState({
      brand: currentUser?.vehicle?.brand || '',
      model: currentUser?.vehicle?.model || '',
      color: currentUser?.vehicle?.color || '',
      plate: currentUser?.vehicle?.plate || '',
      year: currentUser?.vehicle?.year || '',
      seats: currentUser?.vehicle?.seats || 14,
    });
    const [profileVehiclePhotos, setProfileVehiclePhotos] = useState(currentUser?.vehicle?.photos || []);
    const [profileRoutes, setProfileRoutes] = useState({
      primary_route: currentUser?.primary_route || '',
      secondary_route: currentUser?.secondary_route || '',
    });
    const [availableRoutes, setAvailableRoutes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [profileMsg, setProfileMsg] = useState('');
    const [profileError, setProfileError] = useState('');
    const profileVehiclePhotoInputRef = React.useRef(null);

    // Load routes for profile
    React.useEffect(() => {
      const loadRoutes = async () => {
        try {
          const data = await apiService.request('/routes?active=true');
          if (data.success && data.routes) setAvailableRoutes(data.routes);
        } catch (e) { console.error(e); }
      };
      loadRoutes();
    }, []);

    // Refresh full driver data from backend
    React.useEffect(() => {
      const fetchFullProfile = async () => {
        try {
          const data = await apiService.get(`/drivers/${currentUser.driver_id}`, { userType: 'driver' });
          if (data.success && data.driver) {
            const d = data.driver;
            setProfileData({ name: d.name || '', email: d.email || '', phone: d.phone || '' });
            setProfileVehicle({
              brand: d.vehicle?.brand || '', model: d.vehicle?.model || '', color: d.vehicle?.color || '',
              plate: d.vehicle?.plate || '', year: d.vehicle?.year || '', seats: d.vehicle?.seats || 14,
            });
            setProfileVehiclePhotos(d.vehicle?.photos || []);
            setProfileRoutes({ primary_route: d.primary_route || '', secondary_route: d.secondary_route || '' });
          }
        } catch (e) { console.error('Erreur chargement profil:', e); }
      };
      if (currentUser?.driver_id) fetchFullProfile();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleVehiclePhotoFromFile = (e) => {
      const files = Array.from(e.target.files);
      const remaining = 5 - profileVehiclePhotos.length;
      files.slice(0, remaining).forEach(file => {
        if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.src = reader.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxW = 800, maxH = 600;
            let w = img.width, h = img.height;
            if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
            if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            const compressed = canvas.toDataURL('image/jpeg', 0.75);
            setProfileVehiclePhotos(prev => prev.length < 5 ? [...prev, compressed] : prev);
          };
        };
        reader.readAsDataURL(file);
      });
      e.target.value = '';
    };

    const handleSaveProfile = async () => {
      setSaving(true);
      setProfileMsg('');
      setProfileError('');
      try {
        const payload = {
          name: profileData.name,
          email: profileData.email || null,
          vehicle: { ...profileVehicle, photos: profileVehiclePhotos },
          vehiclePhotos: profileVehiclePhotos,
          primary_route: profileRoutes.primary_route || null,
          secondary_route: profileRoutes.secondary_route || null,
        };
        const res = await apiService.put(`/drivers/${currentUser.driver_id}`, payload, { userType: 'driver' });
        if (res.success) {
          setProfileMsg('✅ Profil mis à jour avec succès !');
          // Update local user data
          const updated = { ...currentUser, ...profileData, vehicle: { ...profileVehicle, photos: profileVehiclePhotos }, primary_route: profileRoutes.primary_route, secondary_route: profileRoutes.secondary_route };
          setCurrentUser(updated);
          localStorage.setItem('currentDriver', JSON.stringify(updated));
        } else {
          setProfileError(res.error || 'Erreur lors de la mise à jour');
        }
      } catch (err) {
        setProfileError('Erreur: ' + (err.message || err.error));
      } finally {
        setSaving(false);
      }
    };

    const missingItems = [];
    if (!profileVehiclePhotos || profileVehiclePhotos.length < 3) missingItems.push('Photos du véhicule');
    if (!profileData.email) missingItems.push('Email');
    if (!profileRoutes.primary_route) missingItems.push('Ligne habituelle');

    const sectionStyle = {
      background: 'white', borderRadius: '16px', padding: '1.25rem', marginBottom: '1rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    };
    const sectionTitleStyle = { fontSize: '1.05rem', fontWeight: '600', color: '#1C1C1C', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };
    const inputStyle = {
      width: '100%', padding: '0.75rem 1rem', border: '2px solid #E9EBEF', borderRadius: '10px',
      fontSize: '0.95rem', fontFamily: "'Poppins', sans-serif", marginBottom: '0.75rem', boxSizing: 'border-box'
    };
    const labelStyle = { display: 'block', fontWeight: '500', fontSize: '0.9rem', color: '#414852', marginBottom: '0.25rem' };

    return (
      <div style={{ padding: '1rem', marginBottom: '80px' }}>
        <Stable render={BackButton} key="BackButton" />
        <h2 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#1C1C1C', marginBottom: '1rem' }}>Mon Profil</h2>

        {/* Alert for missing info */}
        {missingItems.length > 0 && (
          <div style={{
            background: '#fef3c7', border: '1px solid #FFB900', borderRadius: '12px',
            padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem'
          }}>
            <AlertCircle size={22} style={{ color: '#FFB900', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ fontWeight: '600', color: '#92400e', fontSize: '0.95rem', margin: 0 }}>Complétez votre profil</p>
              <p style={{ color: '#a16207', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
                {missingItems.join(' · ')}
              </p>
            </div>
          </div>
        )}

        {profileMsg && (
          <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#065f46', fontWeight: '500' }}>
            {profileMsg}
          </div>
        )}
        {profileError && (
          <div style={{ background: '#fee2e2', border: '1px solid #FEE2E2', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991b1b', fontWeight: '500' }}>
            {profileError}
          </div>
        )}

        {/* Section: Info personnelles */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}><Users size={20} /> Informations personnelles</h3>
          <label style={labelStyle}>Nom complet</label>
          <input style={inputStyle} value={profileData.name} onChange={e => setProfileData({...profileData, name: e.target.value})} />
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" value={profileData.email} onChange={e => setProfileData({...profileData, email: e.target.value})} placeholder="votre@email.com" />
          <label style={labelStyle}>Téléphone</label>
          <input style={{...inputStyle, background: '#F4F5F7', color: '#9AA0A8'}} value={profileData.phone} disabled />
        </div>

        {/* Section: Véhicule */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}><Car size={20} /> Véhicule</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label style={labelStyle}>Marque</label>
              <input style={inputStyle} value={profileVehicle.brand} onChange={e => setProfileVehicle({...profileVehicle, brand: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Modèle</label>
              <input style={inputStyle} value={profileVehicle.model} onChange={e => setProfileVehicle({...profileVehicle, model: e.target.value})} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label style={labelStyle}>Couleur</label>
              <input style={inputStyle} value={profileVehicle.color} onChange={e => setProfileVehicle({...profileVehicle, color: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Plaque</label>
              <input style={{...inputStyle, textTransform: 'uppercase'}} value={profileVehicle.plate} onChange={e => setProfileVehicle({...profileVehicle, plate: e.target.value})} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label style={labelStyle}>Année</label>
              <input style={inputStyle} type="number" value={profileVehicle.year} onChange={e => setProfileVehicle({...profileVehicle, year: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Places</label>
              <input style={inputStyle} type="number" value={profileVehicle.seats} onChange={e => setProfileVehicle({...profileVehicle, seats: parseInt(e.target.value) || 14})} />
            </div>
          </div>
        </div>

        {/* Section: Photos du véhicule */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>
            <Camera size={20} /> Photos du véhicule
            <span style={{ marginLeft: 'auto', fontSize: '0.85rem', fontWeight: '500', color: profileVehiclePhotos.length >= 3 ? '#10b981' : '#FFB900' }}>
              {profileVehiclePhotos.length}/5
            </span>
          </h3>
          {profileVehiclePhotos.length < 3 && (
            <p style={{ color: '#FFB900', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              ⚠️ Ajoutez au moins 3 photos pour compléter votre profil
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
            {profileVehiclePhotos.map((vp, idx) => (
              <div key={idx} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '4/3', border: '2px solid #E9EBEF' }}>
                <img src={vp} alt={`Véhicule ${idx+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" onClick={() => setProfileVehiclePhotos(prev => prev.filter((_, i) => i !== idx))}
                  style={{
                    position: 'absolute', top: '3px', right: '3px', background: 'rgba(239,68,68,0.9)',
                    color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', fontWeight: '700'
                  }}>✕</button>
              </div>
            ))}
            {profileVehiclePhotos.length < 5 && (
              <div
                onClick={() => profileVehiclePhotoInputRef.current.click()}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  border: '2px dashed #DCE0E6', borderRadius: '10px', aspectRatio: '4/3', cursor: 'pointer', background: '#FFFFFF'
                }}
              >
                <Camera size={24} style={{ color: '#9AA0A8' }} />
                <span style={{ fontSize: '0.75rem', color: '#5C636E', marginTop: '0.25rem' }}>Ajouter</span>
              </div>
            )}
          </div>
          <input ref={profileVehiclePhotoInputRef} type="file" accept="image/*" multiple onChange={handleVehiclePhotoFromFile} style={{ display: 'none' }} />
        </div>

        {/* Section: Lignes */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}><MapPin size={20} /> Lignes de travail</h3>
          <label style={labelStyle}>Ligne habituelle</label>
          <select style={inputStyle} value={profileRoutes.primary_route} onChange={e => setProfileRoutes({...profileRoutes, primary_route: e.target.value})}>
            <option value="">-- Sélection --</option>
            {availableRoutes.map(r => <option key={r.route_id} value={r.route_id}>{r.name}</option>)}
          </select>
          <label style={labelStyle}>Ligne secondaire</label>
          <select style={inputStyle} value={profileRoutes.secondary_route} onChange={e => setProfileRoutes({...profileRoutes, secondary_route: e.target.value})}>
            <option value="">-- Aucune --</option>
            {availableRoutes.map(r => <option key={r.route_id} value={r.route_id}>{r.name}</option>)}
          </select>
        </div>

        {/* Save button */}
        <button
          onClick={handleSaveProfile}
          disabled={saving}
          style={{
            width: '100%', padding: '1rem', background: saving ? '#9AA0A8' : '#F54119',
            color: 'white', border: 'none', borderRadius: '12px', fontSize: '1.05rem',
            fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            fontFamily: "'Poppins', sans-serif", transition: 'background 0.2s'
          }}
        >
          <Save size={20} />
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
    );
  };

  // ==============================================
  // TAXI DRIVER PAGE
  // ==============================================
  const TaxiDriverPage = () => {
    // taxiTab is in parent scope to persist across re-renders
    const [availableRides, setAvailableRides] = useState([]);
    const [activeRide, setActiveRide] = useState(null);
    const [rideHistory, setRideHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);
    const loadingActionRef = useRef(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatText, setChatText] = useState('');
    const [showChat, setShowChat] = useState(false);
    const chatMessagesCountRef = useRef(0);
    const [chatNotifBadge, setChatNotifBadge] = useState(false);
    const [taxiError, setTaxiError] = useState('');
    const [driverPosition, setDriverPosition] = useState(null);
    const [directionsResult, setDirectionsResult] = useState(null);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [completedRideForRating, setCompletedRideForRating] = useState(null);
    const [driverRatingValue, setDriverRatingValue] = useState(0);
    const [driverRatingTags, setDriverRatingTags] = useState([]);
    const [driverRatingComment, setDriverRatingComment] = useState('');
    const [driverRatingCriteria, setDriverRatingCriteria] = useState(null);
    const pollRef = useRef(null);
    const chatPollRef = useRef(null);
    const availablePollRef = useRef(null);
    const activeRideRef = useRef(null);
    const geoWatchRef = useRef(null);
    const directionsServiceRef = useRef(null);
    const mapRef = useRef(null);

    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const GOOGLE_MAPS_LIBS = React.useMemo(() => ['places', 'geometry'], []);
    const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBS });

    const statusLabels = {
      searching: t.availableRides,
      accepted: '✅ ' + t.accept,
      driver_arriving: '📍 ' + t.arriving,
      in_progress: '🚗 ' + t.activeRide,
      completed: '🎉 Terminée',
      cancelled_client: '❌ Annulée (client)',
      cancelled_driver: '❌ Annulée (vous)',
      no_driver_found: '😞 Pas de chauffeur'
    };

    const distanceLabels = {
      short: '1-3 km',
      medium: '3-7 km',
      long: '7-15 km',
      very_long: '15+ km'
    };

    // Sync activeRide to ref to avoid stale closure in interval
    React.useEffect(() => {
      activeRideRef.current = activeRide;
    }, [activeRide]);

    React.useEffect(() => {
      checkActiveRide();
      // Auto-refresh available rides every 15 seconds
      availablePollRef.current = setInterval(() => {
        if (!activeRideRef.current && !rateLimitBackoffRef.current) loadAvailable();
      }, 15000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (chatPollRef.current) clearInterval(chatPollRef.current);
        if (availablePollRef.current) clearInterval(availablePollRef.current);
        if (geoWatchRef.current) navigator.geolocation.clearWatch(geoWatchRef.current);
      };
    }, []);

    const checkActiveRide = async () => {
      try {
        const res = await apiService.get(`/taxi/driver/${currentUser.driver_id}/active`, { userType: 'driver' });
        if (res.ride) {
          setActiveRide(res.ride);
          setTaxiTab('active');
          startPolling(res.ride.ride_id);
          startDriverTracking();
        } else {
          loadAvailable();
        }
      } catch (err) {
        loadAvailable();
      }
    };

    const loadAvailable = async () => {
      if (rateLimitBackoffRef.current) return;
      setLoading(true);
      try {
        const res = await apiService.get('/taxi/available', { userType: 'driver' });
        if (res.success) {
          // Vérifier si le chauffeur est bloqué
          if (res.blocked) {
            setIsDriverBlocked(true);
            setAvailableRides([]);
          } else {
            setAvailableRides(res.rides || []);
          }
        }
      } catch (err) {
        if (err.status === 429) {
          rateLimitBackoffRef.current = true;
          setTimeout(() => { rateLimitBackoffRef.current = false; }, 60000);
        }
      }
      setLoading(false);
    };

    const acceptRide = async (rideId) => {
      setLoadingAction(true);
      setTaxiError('');
      try {
        const res = await apiService.post(`/taxi/${rideId}/accept`, {}, { userType: 'driver' });
        if (res.success) {
          setActiveRide(res.ride);
          setAvailableRides([]); // Clear available list
          setTaxiTab('active');
          startPolling(rideId);
          startDriverTracking(); // Start GPS tracking
        } else {
          setTaxiError(res.error || 'Erreur');
        }
      } catch (err) {
        setTaxiError(err.error || err.message || 'Cette course a déjà été prise');
        loadAvailable();
      }
      setLoadingAction(false);
    };

    const declineRide = async (rideId) => {
      try {
        await apiService.post(`/taxi/${rideId}/decline`, {}, { userType: 'driver' });
        setAvailableRides(prev => prev.filter(r => r.ride_id !== rideId));
      } catch (err) { /* ignore */ }
    };

    const updateStatus = async (newStatus, finalPrice = null) => {
      if (!activeRide) return;
      // Prevent double-clicks
      if (loadingActionRef.current) return;
      loadingActionRef.current = true;
      setLoadingAction(true);
      setTaxiError('');
      try {
        const body = { status: newStatus };
        if (finalPrice) body.final_price = finalPrice;

        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        const res = await apiService.patch(`/taxi/${activeRide.ride_id}/status`, body, { userType: 'driver' });
        clearTimeout(timeout);
        if (res.success) {
          setActiveRide(res.ride);
          // Auto-send chat message when driver arrives
          if (newStatus === 'driver_arriving') {
            try {
              await apiService.post(`/taxi/${activeRide.ride_id}/messages`, { text: '📍 Je suis arrivé au point de prise en charge' }, { userType: 'driver' });
              loadMessages();
            } catch (e) { /* ignore */ }
          }
          if (newStatus === 'completed' || newStatus === 'cancelled_driver') {
            if (pollRef.current) clearInterval(pollRef.current);
            if (chatPollRef.current) clearInterval(chatPollRef.current);
            stopDriverTracking();
            setDirectionsResult(null);
            setChatMessages([]);
            chatMessagesCountRef.current = 0;
            if (newStatus === 'completed') {
              // Sauvegarder la course pour évaluation optionnelle
              setCompletedRideForRating(res.ride);
              setShowRatingModal(true);
              // Charger les critères
              apiService.get('/ratings/criteria?role=driver')
                .then(r => { if (r.success) setDriverRatingCriteria(r.criteria); })
                .catch(() => {});
            }
            setTaxiTab('available');
            setActiveRide(null);
            loadAvailable();
          }
        } else {
          setTaxiError(res.error || 'Erreur');
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          setTaxiError('Délai dépassé. Réessayez.');
        } else {
          setTaxiError(err.error || err.message || 'Erreur lors de la mise à jour');
        }
      } finally {
        setLoadingAction(false);
        loadingActionRef.current = false;
      }
    };

    const startPolling = (rideId) => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        if (rateLimitBackoffRef.current) return;
        try {
          const res = await apiService.get(`/taxi/${rideId}`, { userType: 'driver' });
          if (res.success) {
            setActiveRide(res.ride);
            if (['cancelled_client', 'no_driver_found'].includes(res.ride.status)) {
              clearInterval(pollRef.current);
              stopDriverTracking();
              setActiveRide(null);
              setDirectionsResult(null);
              setTaxiTab('available');
              setTaxiError(statusLabels[res.ride.status]);
              loadAvailable();
            }
          }
        } catch (err) {
          if (err.status === 429) {
            rateLimitBackoffRef.current = true;
            setTimeout(() => { rateLimitBackoffRef.current = false; }, 60000);
          }
        }
      }, 10000);
    };

    // ——————————————————————————
    // GPS TRACKING — driver real-time position
    // ——————————————————————————
    const lastSentPosRef = React.useRef(null);

    const sendPositionToServer = async (pos) => {
      const ride = activeRideRef.current;
      if (!ride || rateLimitBackoffRef.current) return;
      // Only send if moved > 30m to limit requests
      if (lastSentPosRef.current) {
        const dlat = pos.lat - lastSentPosRef.current.lat;
        const dlng = pos.lng - lastSentPosRef.current.lng;
        const dist = Math.sqrt(dlat * dlat + dlng * dlng) * 111000; // approx meters
        if (dist < 30) return;
      }
      try {
        await apiService.post(`/taxi/${ride.ride_id}/location`, { lat: pos.lat, lng: pos.lng }, { userType: 'driver' });
        lastSentPosRef.current = pos;
      } catch (err) { /* non-bloquant */ }
    };

    const startDriverTracking = () => {
      if (!navigator.geolocation) return;
      if (geoWatchRef.current) navigator.geolocation.clearWatch(geoWatchRef.current);
      lastSentPosRef.current = null;
      geoWatchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setDriverPosition(newPos);
          sendPositionToServer(newPos);
        },
        (err) => console.warn('GPS error:', err.message),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
      );
    };

    const stopDriverTracking = () => {
      if (geoWatchRef.current) {
        navigator.geolocation.clearWatch(geoWatchRef.current);
        geoWatchRef.current = null;
      }
      setDriverPosition(null);
      setDirectionsResult(null);
    };

    // Calculate route from driver to destination
    const calculateDriverRoute = useCallback((origin, destination) => {
      if (!directionsServiceRef.current || !origin || !destination) return;
      directionsServiceRef.current.route(
        {
          origin,
          destination,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK') {
            setDirectionsResult(result);
          }
        }
      );
    }, []);

    // When driver position or ride status changes, recalculate route
    React.useEffect(() => {
      if (!driverPosition || !activeRide || !isLoaded) return;
      const target = activeRide.status === 'accepted' || activeRide.status === 'driver_arriving'
        ? activeRide.pickup  // Navigate to pickup
        : activeRide.status === 'in_progress'
          ? activeRide.dropoff  // Navigate to dropoff
          : null;
      if (target?.lat && target?.lng) {
        calculateDriverRoute(driverPosition, { lat: target.lat, lng: target.lng });
      }
    }, [driverPosition, activeRide?.status, isLoaded]);

    const onDriverMapLoad = useCallback((map) => {
      mapRef.current = map;
      if (window.google) {
        directionsServiceRef.current = new window.google.maps.DirectionsService();
      }
    }, []);

    const sendMessage = async () => {
      if (!chatText.trim() || !activeRide) return;
      try {
        await apiService.post(`/taxi/${activeRide.ride_id}/messages`, { text: chatText }, { userType: 'driver' });
        setChatText('');
        loadMessages();
      } catch (err) { /* ignore */ }
    };

    const loadMessages = async () => {
      if (!activeRide) return;
      try {
        const res = await apiService.get(`/taxi/${activeRide.ride_id}/messages`, { userType: 'driver' });
        if (res.success) {
          // Detect new messages from client → notification
          const newCount = res.messages.length;
          if (newCount > chatMessagesCountRef.current && chatMessagesCountRef.current > 0) {
            // Check if last message is from client
            const lastMsg = res.messages[res.messages.length - 1];
            if (lastMsg && lastMsg.sender_type === 'client') {
              playNotificationSound();
              if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
              if (!showChat) setChatNotifBadge(true);
            }
          }
          chatMessagesCountRef.current = newCount;
          setChatMessages(res.messages);
        }
      } catch (err) { /* ignore */ }
    };

    const loadHistory = async () => {
      setLoading(true);
      setTaxiError('');
      try {
        const res = await apiService.get(`/taxi/driver/${currentUser.driver_id}/history`, { userType: 'driver' });
        if (res.success) {
          setRideHistory(res.rides || []);
        } else {
          console.error('Erreur historique:', res.error);
          setTaxiError(res.error || 'Erreur chargement historique');
        }
      } catch (err) {
        console.error('Erreur chargement historique courses:', err);
        setTaxiError('Erreur chargement historique');
      }
      setLoading(false);
    };

    React.useEffect(() => {
      if (showChat) setChatNotifBadge(false);
    }, [showChat]);

    // Poll messages even when chat is closed (for notifications)
    React.useEffect(() => {
      if (activeRide) {
        loadMessages();
        chatPollRef.current = setInterval(loadMessages, 15000);
        return () => { if (chatPollRef.current) clearInterval(chatPollRef.current); };
      }
    }, [activeRide?.ride_id]);

    const sectionStyle = {
      background: 'white',
      borderRadius: '16px',
      padding: '1.5rem',
      marginBottom: '1rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    };

    const inputStyle = {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #E9EBEF',
      borderRadius: '12px',
      fontSize: '1rem',
      outline: 'none',
      boxSizing: 'border-box'
    };

    return (
      <div style={{ padding: '1rem', maxWidth: '500px', margin: '0 auto' }}>
        <Stable render={BackButton} key="BackButton" />
        <h2 style={{ margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Car size={24} color="#FFB900" /> {t.taxiRides}
        </h2>

        {taxiError && (
          <div style={{ background: '#fef2f2', border: '1px solid #FEE2E2', borderRadius: '12px', padding: '12px 16px', marginBottom: '1rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={18} /> {taxiError}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
          {[
            { key: 'available', label: t.availableRides, icon: <Navigation size={16} /> },
            { key: 'active', label: t.activeRide, icon: <Car size={16} /> },
            { key: 'history', label: t.rideHistory, icon: <Clock size={16} /> }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setTaxiTab(tab.key);
                setTaxiError('');
                if (tab.key === 'available') loadAvailable();
                if (tab.key === 'active') checkActiveRide();
                if (tab.key === 'history') loadHistory();
              }}
              style={{
                flex: 1, padding: '10px 8px', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer',
                fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                background: taxiTab === tab.key ? '#FFB900' : '#F4F5F7',
                color: taxiTab === tab.key ? 'white' : '#5C636E'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ======== COURSES DISPONIBLES ======== */}
        {taxiTab === 'available' && (
          <div>
            {/* Mini toggle disponibilité */}
            <div
              onClick={() => toggleAvailability()}
              style={{
                background: isDriverAvailable
                  ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)'
                  : 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                border: `2px solid ${isDriverAvailable ? '#22c55e' : '#FEE2E2'}`,
                borderRadius: '14px',
                padding: '12px 16px',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: togglingAvailability ? 'wait' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: togglingAvailability ? 0.7 : 1
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  background: isDriverAvailable ? '#22c55e' : '#DC2626',
                  boxShadow: isDriverAvailable ? '0 0 8px rgba(34,197,94,0.5)' : 'none',
                  animation: isDriverAvailable ? 'pulse 2s infinite' : 'none'
                }} />
                <span style={{ fontWeight: '600', fontSize: '0.9rem', color: isDriverAvailable ? '#15803d' : '#dc2626' }}>
                  {isDriverAvailable ? t.driverOnline : t.driverOffline}
                </span>
              </div>
              <div style={{
                width: '44px', height: '24px', borderRadius: '12px',
                background: isDriverAvailable ? '#22c55e' : '#DCE0E6',
                position: 'relative', transition: 'background 0.3s ease',
                flexShrink: 0
              }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: 'white', position: 'absolute', top: '2px',
                  left: isDriverAvailable ? '22px' : '2px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </div>
            </div>

            {/* Message hors ligne */}
            {!isDriverAvailable && (
              <div style={{ ...sectionStyle, textAlign: 'center', color: '#9AA0A8', borderTop: '3px solid #DC2626' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                  <Car size={32} style={{ color: '#DC2626' }} />
                </div>
                <p style={{ fontWeight: '600', color: '#414852', fontSize: '1rem' }}>{t.driverOffline}</p>
                <p style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>{t.offlineMessage}</p>
                <button
                  onClick={() => toggleAvailability(true)}
                  disabled={togglingAvailability}
                  style={{
                    padding: '12px 28px', background: '#22c55e', color: 'white', border: 'none',
                    borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '1rem',
                    display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto'
                  }}
                >
                  <Car size={20} /> {t.goOnline}
                </button>
              </div>
            )}

            {isDriverAvailable && loading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#FFB900' }}>
                <Loader size={28} className="spin" />
              </div>
            )}

            {isDriverAvailable && !loading && availableRides.length === 0 && (
              <div style={{ ...sectionStyle, textAlign: 'center', color: '#9AA0A8' }}>
                <Car size={48} style={{ marginBottom: '0.5rem', opacity: 0.3 }} />
                <p style={{ fontWeight: '600' }}>{t.noAvailableRides}</p>
                <p style={{ fontSize: '0.85rem' }}>Les nouvelles courses apparaîtront ici</p>
                <button
                  onClick={loadAvailable}
                  style={{ marginTop: '1rem', padding: '10px 24px', background: '#FFB900', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Rafraîchir
                </button>
              </div>
            )}

            {availableRides.map(ride => {
              const rideTypeColors = { 'COURSE COVOITURAGE': '#22c55e', 'COURSE CONFORT': '#FF7800', 'COURSE PREMIUM': '#FFB900', 'COURSE MOTO': '#f97316' };
              const rideBorderColor = rideTypeColors[ride.ride_type_label] || '#FFB900';
              return (
              <div key={ride.ride_id} style={{ ...sectionStyle, borderLeft: `4px solid ${rideBorderColor}` }}>
                {ride.ride_type_label && (
                  <div style={{ marginBottom: '8px', display: 'inline-block', padding: '3px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '700', color: 'white', background: rideBorderColor, letterSpacing: '0.5px' }}>
                    {ride.ride_type_label}
                  </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontWeight: '600', color: '#1C1C1C', marginBottom: '4px' }}>
                    📍 {ride.pickup?.address}
                  </div>
                  <div style={{ fontWeight: '600', color: '#1C1C1C' }}>
                    🏁 {ride.dropoff?.address}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#5C636E' }}>
                    📏 {distanceLabels[ride.estimated_distance] || ride.estimated_distance}
                  </span>
                  <span style={{ fontSize: '1.2rem', fontWeight: '700', color: '#C9350F' }}>
                    {ride.estimated_price?.toLocaleString('fr-FR')} {ride.currency || 'CDF'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.8rem', color: '#5C636E' }}>
                    {ride.payment_method === 'cash' ? '💵 Cash' : '📱 Mobile Money'}
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#5C636E' }}>
                    👤 {ride.client?.name || 'Client'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => declineRide(ride.ride_id)}
                    style={{ flex: 1, padding: '12px', border: '2px solid #DC2626', background: 'transparent', color: '#DC2626', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    {t.decline}
                  </button>
                  <button
                    onClick={() => acceptRide(ride.ride_id)}
                    disabled={loadingAction}
                    style={{ flex: 2, padding: '12px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  >
                    {loadingAction ? <Loader size={18} className="spin" /> : <><CheckCircle size={18} /> {t.accept}</>}
                  </button>
                </div>
              </div>
              );
            })}

            {/* Auto-refresh */}
            {!loading && availableRides.length > 0 && (
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={loadAvailable}
                  style={{ padding: '10px 24px', background: '#F4F5F7', border: 'none', borderRadius: '12px', color: '#5C636E', fontWeight: '600', cursor: 'pointer' }}
                >
                  🔄 Rafraîchir
                </button>
              </div>
            )}
          </div>
        )}

        {/* ======== COURSE ACTIVE ======== */}
        {taxiTab === 'active' && (
          <div>
            {!activeRide && (
              <div style={{ ...sectionStyle, textAlign: 'center', color: '#9AA0A8' }}>
                <Car size={48} style={{ marginBottom: '0.5rem', opacity: 0.3 }} />
                <p>{t.noAvailableRides}</p>
              </div>
            )}

            {activeRide && (
              <div>
                {/* Carte temps réel */}
                {isLoaded && (
                  <div style={{ height: '250px', borderRadius: '16px', overflow: 'hidden', marginBottom: '1rem', border: '2px solid #FFB900' }}>
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={driverPosition || { lat: activeRide.pickup?.lat || -2.5, lng: activeRide.pickup?.lng || 28.86 }}
                      zoom={14}
                      options={{
                        disableDefaultUI: true,
                        zoomControl: true,
                        gestureHandling: 'greedy',
                        styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }]
                      }}
                      onLoad={onDriverMapLoad}
                    >
                      {/* Position du chauffeur */}
                      {driverPosition && (
                        <Marker
                          position={driverPosition}
                          icon={{
                            path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 0,
                            scale: 7,
                            fillColor: '#FF7800',
                            fillOpacity: 1,
                            strokeColor: '#C9350F',
                            strokeWeight: 2,
                            rotation: 0
                          }}
                          title="Ma position"
                        />
                      )}
                      {/* Point de prise en charge */}
                      {activeRide.pickup?.lat && (
                        <Marker
                          position={{ lat: activeRide.pickup.lat, lng: activeRide.pickup.lng }}
                          label={{ text: 'A', color: 'white', fontWeight: 'bold' }}
                          icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 12,
                            fillColor: '#22c55e',
                            fillOpacity: 1,
                            strokeColor: '#15803d',
                            strokeWeight: 2
                          }}
                          title={activeRide.pickup.address || 'Prise en charge'}
                        />
                      )}
                      {/* Point de destination */}
                      {activeRide.dropoff?.lat && (
                        <Marker
                          position={{ lat: activeRide.dropoff.lat, lng: activeRide.dropoff.lng }}
                          label={{ text: 'B', color: 'white', fontWeight: 'bold' }}
                          icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 12,
                            fillColor: '#DC2626',
                            fillOpacity: 1,
                            strokeColor: '#b91c1c',
                            strokeWeight: 2
                          }}
                          title={activeRide.dropoff.address || 'Destination'}
                        />
                      )}
                      {/* Itinéraire */}
                      {directionsResult && (
                        <DirectionsRenderer
                          directions={directionsResult}
                          options={{
                            suppressMarkers: true,
                            polylineOptions: {
                              strokeColor: '#FFB900',
                              strokeWeight: 5,
                              strokeOpacity: 0.8
                            }
                          }}
                        />
                      )}
                    </GoogleMap>
                  </div>
                )}

                {/* Bouton Navigation Google Maps */}
                {(() => {
                  const isGoingToPickup = ['accepted', 'driver_arriving'].includes(activeRide.status);
                  const dest = isGoingToPickup ? activeRide.pickup : activeRide.dropoff;
                  const destLabel = isGoingToPickup ? 'au client' : 'à destination';
                  if (!dest?.lat || !dest?.lng) return null;
                  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}&travelmode=driving`;
                  return (
                    <a
                      href={navUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        width: '100%',
                        padding: '14px',
                        marginBottom: '1rem',
                        background: 'linear-gradient(135deg, #1a73e8, #0d47a1)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '14px',
                        fontWeight: '700',
                        fontSize: '1rem',
                        textDecoration: 'none',
                        boxShadow: '0 4px 12px rgba(26,115,232,0.4)'
                      }}
                    >
                      <Navigation size={22} />
                      Naviguer {destLabel}
                    </a>
                  );
                })()}

                {/* Statut */}
                <div style={{ ...sectionStyle, background: '#fffbeb', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#92400e', marginBottom: '8px' }}>
                    {statusLabels[activeRide.status] || activeRide.status}
                  </div>
                  <div style={{ color: '#414852', marginBottom: '4px' }}>
                    <strong>De:</strong> {activeRide.pickup?.address}
                  </div>
                  <div style={{ color: '#414852' }}>
                    <strong>À:</strong> {activeRide.dropoff?.address}
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: '600', color: '#C9350F' }}>
                      {activeRide.estimated_price?.toLocaleString('fr-FR')} CDF
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#5C636E' }}>
                      {activeRide.payment_method === 'cash' ? '💵 Cash' : '📱 Mobile Money'}
                    </span>
                  </div>
                </div>

                {/* Info client */}
                {activeRide.client && (
                  <div style={sectionStyle}>
                    <h4 style={{ margin: '0 0 0.5rem' }}>👤 Client</h4>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontWeight: '700' }}>{activeRide.client.name}</div>
                        {activeRide.client.phone && (
                          <div style={{ fontSize: '0.85rem', color: '#5C636E' }}>{activeRide.client.phone}</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {activeRide.client.phone && (
                          <a href={`tel:${activeRide.client.phone}`} style={{ padding: '10px', background: '#ecfdf5', color: '#059669', borderRadius: '12px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <Phone size={20} />
                          </a>
                        )}
                        <button
                          onClick={() => setShowChat(!showChat)}
                          style={{ padding: '10px', background: showChat ? '#FFB900' : '#fffbeb', color: showChat ? 'white' : '#FFB900', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative' }}
                        >
                          <MessageCircle size={20} />
                          {chatNotifBadge && !showChat && (
                            <span style={{ position: 'absolute', top: '-4px', right: '-4px', width: '14px', height: '14px', background: '#DC2626', borderRadius: '50%', border: '2px solid white', animation: 'pulseGlow 1.5s ease-in-out infinite' }} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Chat */}
                {showChat && (
                  <div style={sectionStyle}>
                    <h4 style={{ margin: '0 0 0.5rem' }}>💬 Chat</h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '10px' }}>
                      {chatMessages.length === 0 && (
                        <p style={{ color: '#9AA0A8', textAlign: 'center', fontSize: '0.85rem' }}>Aucun message</p>
                      )}
                      {chatMessages.map((msg, i) => (
                        <div key={i} style={{ marginBottom: '8px', textAlign: msg.sender_type === 'driver' ? 'right' : 'left' }}>
                          <div style={{
                            display: 'inline-block', padding: '8px 12px', borderRadius: '12px', maxWidth: '80%', fontSize: '0.9rem',
                            background: msg.sender_type === 'driver' ? '#FFB900' : '#F4F5F7',
                            color: msg.sender_type === 'driver' ? 'white' : '#414852'
                          }}>
                            {msg.text}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#9AA0A8', marginTop: '2px' }}>
                            {new Date(msg.sent_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        style={{ ...inputStyle, flex: 1 }}
                        placeholder="Votre message..."
                        value={chatText}
                        onChange={e => setChatText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMessage()}
                      />
                      <button
                        onClick={sendMessage}
                        style={{ padding: '12px', background: '#FFB900', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer' }}
                      >
                        <Send size={20} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Boutons d'action selon le statut */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {activeRide.status === 'accepted' && (
                    <>
                      <button
                        onClick={() => updateStatus('driver_arriving')}
                        disabled={loadingAction}
                        style={{ width: '100%', padding: '16px', background: '#FF7800', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      >
                        {loadingAction ? <Loader size={20} className="spin" /> : <><Navigation size={20} /> {t.arriving}</>}
                      </button>
                      <button
                        onClick={() => updateStatus('cancelled_driver')}
                        style={{ width: '100%', padding: '14px', border: '2px solid #DC2626', background: 'transparent', color: '#DC2626', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {t.cancelRide}
                      </button>
                    </>
                  )}

                  {activeRide.status === 'driver_arriving' && (
                    <>
                      <button
                        onClick={() => updateStatus('in_progress')}
                        disabled={loadingAction}
                        style={{ width: '100%', padding: '16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      >
                        {loadingAction ? <Loader size={20} className="spin" /> : <><Car size={20} /> {t.startRide}</>}
                      </button>
                      <button
                        onClick={() => updateStatus('cancelled_driver')}
                        style={{ width: '100%', padding: '14px', border: '2px solid #DC2626', background: 'transparent', color: '#DC2626', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {t.cancelRide}
                      </button>
                    </>
                  )}

                  {activeRide.status === 'in_progress' && (
                    <button
                      onClick={() => updateStatus('completed', activeRide.estimated_price)}
                      disabled={loadingAction}
                      style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #FFB900, #FF9B00)', color: 'white', border: 'none', borderRadius: '16px', fontWeight: '700', cursor: 'pointer', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      {loadingAction ? <Loader size={20} className="spin" /> : <><CheckCircle size={22} /> {t.endRide}</>}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ======== HISTORIQUE ======== */}
        {taxiTab === 'history' && (
          <div>
            {loading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#FFB900' }}>
                <Loader size={28} className="spin" />
              </div>
            )}

            {!loading && rideHistory.length === 0 && (
              <div style={{ ...sectionStyle, textAlign: 'center', color: '#9AA0A8' }}>
                <Car size={48} style={{ marginBottom: '0.5rem', opacity: 0.3 }} />
                <p>Aucune course pour le moment</p>
              </div>
            )}

            {rideHistory.map(ride => (
              <div key={ride.ride_id} style={{ ...sectionStyle, borderLeft: ride.status === 'completed' ? '4px solid #22c55e' : '4px solid #DC2626' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontWeight: '600' }}>{statusLabels[ride.status] || ride.status}</span>
                  <span style={{ color: '#5C636E', fontSize: '0.8rem' }}>
                    {new Date(ride.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#414852' }}>
                  {ride.pickup?.address} → {ride.dropoff?.address}
                </div>
                <div style={{ marginTop: '6px', fontWeight: '600', color: '#C9350F' }}>
                  {(ride.final_price || ride.estimated_price)?.toLocaleString('fr-FR')} CDF
                </div>
                {ride.rating_by_client && (
                  <div style={{ color: '#FFB900', fontSize: '0.85rem', marginTop: '2px' }}>
                    {'⭐'.repeat(ride.rating_by_client)} ({ride.rating_by_client}/5)
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ═══════════ MODAL: Évaluer le client (optionnel) ═══════════ */}
        {showRatingModal && completedRideForRating && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflow: 'auto', padding: '2rem', color: '#1C1C1C' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✅</div>
                <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem' }}>Course terminée</h3>
                <p style={{ color: '#5C636E', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  {(completedRideForRating.final_price || completedRideForRating.estimated_price)?.toLocaleString('fr-FR')} CDF
                </p>

                <h4 style={{ marginBottom: '0.75rem', color: '#414852', fontSize: '1rem' }}>Évaluer ce client</h4>

                {/* Étoiles */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '1rem' }}>
                  {[1, 2, 3, 4, 5].map(v => (
                    <button
                      key={v}
                      onClick={() => setDriverRatingValue(v)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '2rem', opacity: v <= driverRatingValue ? 1 : 0.25, transition: 'all 0.2s', transform: v <= driverRatingValue ? 'scale(1.1)' : 'scale(1)' }}
                    >
                      ⭐
                    </button>
                  ))}
                </div>

                {/* Tags */}
                {driverRatingValue > 0 && driverRatingCriteria && (
                  <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                    <p style={{ fontWeight: '600', fontSize: '0.85rem', color: '#414852', marginBottom: '8px' }}>
                      {driverRatingValue >= 4 ? '👍 Points positifs' : '👎 Points négatifs'}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {(driverRatingValue >= 4 ? driverRatingCriteria.positive : driverRatingCriteria.negative).map(tag => {
                        const selected = driverRatingTags.includes(tag.key);
                        return (
                          <button
                            key={tag.key}
                            onClick={() => {
                              setDriverRatingTags(prev =>
                                selected ? prev.filter(t => t !== tag.key) : [...prev, tag.key]
                              );
                            }}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '20px',
                              border: selected ? '2px solid #1C1C1C' : '2px solid #E9EBEF',
                              background: selected ? '#1C1C1C' : 'white',
                              color: selected ? 'white' : '#414852',
                              fontSize: '0.82rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {tag.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Commentaire */}
                <textarea
                  placeholder="Commentaire (optionnel)"
                  value={driverRatingComment}
                  onChange={e => setDriverRatingComment(e.target.value)}
                  style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', marginBottom: '1rem', width: '100%', color: '#1C1C1C' }}
                />

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setShowRatingModal(false);
                      setCompletedRideForRating(null);
                      setDriverRatingValue(0);
                      setDriverRatingTags([]);
                      setDriverRatingComment('');
                      setDriverRatingCriteria(null);
                    }}
                    style={{ flex: 1, padding: '12px', border: '2px solid #E9EBEF', background: 'white', borderRadius: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem', color: '#414852' }}
                  >
                    Passer
                  </button>
                  <button
                    onClick={async () => {
                      if (!driverRatingValue) return;
                      try {
                        await apiService.post(`/taxi/${completedRideForRating.ride_id}/rate/driver`, {
                          rating: driverRatingValue,
                          tags: driverRatingTags,
                          comment: driverRatingComment
                        }, { userType: 'driver' });
                      } catch (e) { /* ignore */ }
                      setShowRatingModal(false);
                      setCompletedRideForRating(null);
                      setDriverRatingValue(0);
                      setDriverRatingTags([]);
                      setDriverRatingComment('');
                      setDriverRatingCriteria(null);
                    }}
                    disabled={!driverRatingValue}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: driverRatingValue ? '#1C1C1C' : '#DCE0E6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '14px',
                      fontWeight: '600',
                      cursor: driverRatingValue ? 'pointer' : 'not-allowed',
                      fontSize: '0.9rem'
                    }}
                  >
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========== PAGE MON SOLDE ==========
  const MyBalancePage = () => {
    const [balanceSummary, setBalanceSummary] = useState(null);
    const [balanceTransactions, setBalanceTransactions] = useState([]);
    const [balanceRidesList, setBalanceRidesList] = useState(null);
    const [balPeriod, setBalPeriod] = useState('week');
    const [balTab, setBalTab] = useState('summary'); // summary | transactions | rides
    const [loadingBal, setLoadingBal] = useState(true);

    const fetchBalanceData = async () => {
      if (!currentUser?.driver_id) return;
      setLoadingBal(true);
      try {
        const [sumRes, txRes, ridesRes] = await Promise.all([
          apiService.get(`/balance/driver/${currentUser.driver_id}/summary`, { userType: 'driver' }),
          apiService.get(`/balance/driver/${currentUser.driver_id}/transactions?period=${balPeriod}&limit=50`, { userType: 'driver' }),
          apiService.get(`/balance/driver/${currentUser.driver_id}/rides?period=${balPeriod}`, { userType: 'driver' })
        ]);
        if (sumRes.success) setBalanceSummary(sumRes);
        if (txRes.success) setBalanceTransactions(txRes.transactions || []);
        if (ridesRes.success) setBalanceRidesList(ridesRes);
      } catch (error) {
        console.error('Erreur solde:', error);
      } finally {
        setLoadingBal(false);
      }
    };

    useEffect(() => {
      fetchBalanceData();
    }, [balPeriod]);

    const backButtonStyle = {
      display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
      background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '10px',
      color: 'white', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', marginBottom: '16px'
    };

    const txTypeLabels = {
      mm_credit: { label: '📱 Crédit MM', color: '#059669' },
      cash_debit: { label: '💵 Débit Cash', color: '#dc2626' },
      weekly_payout: { label: '💸 Virement', color: '#F54119' },
      cancellation_penalty: { label: '❌ Pénalité', color: '#dc2626' },
      cancellation_credit: { label: '✅ Crédit annulation', color: '#059669' },
      admin_adjustment: { label: '🔧 Ajustement', color: '#FFB900' },
      debt_recovery: { label: '🔄 Recouvrement', color: '#C9350F' },
      manual_payment: { label: '💳 Paiement', color: '#059669' },
      supplier_share_debit: { label: '🤝 Part fournisseur', color: '#dc2626' }
    };

    if (loadingBal) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: 'white' }}>
          <button onClick={() => setCurrentPage('home')} style={backButtonStyle}><ArrowLeft size={18} /> {t.home}</button>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '12px' }}>Chargement...</p>
        </div>
      );
    }

    return (
      <div style={{ padding: '20px', color: 'white' }}>
        <button onClick={() => setCurrentPage('home')} style={backButtonStyle}><ArrowLeft size={18} /> {t.home}</button>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '16px' }}>💰 {t.myBalance}</h2>

        {/* Solde actuel */}
        <div style={{
          padding: '1.5rem', borderRadius: '16px', textAlign: 'center', marginBottom: '16px',
          background: (balanceSummary?.current_balance || 0) >= 0
            ? 'linear-gradient(135deg, rgba(16,185,129,0.3), rgba(5,150,105,0.2))'
            : 'linear-gradient(135deg, rgba(239,68,68,0.3), rgba(220,38,38,0.2))',
          border: `2px solid ${(balanceSummary?.current_balance || 0) >= 0 ? 'rgba(16,185,129,0.5)' : 'rgba(239,68,68,0.5)'}`
        }}>
          <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>{t.currentBalance}</p>
          <p style={{ fontSize: '2.5rem', fontWeight: '800', margin: '4px 0' }}>
            {(balanceSummary?.current_balance || 0) >= 0 ? '+' : ''}{(balanceSummary?.current_balance || 0).toLocaleString('fr-FR')} CDF
          </p>
          {balanceSummary?.account_blocked && (
            <div style={{
              background: 'rgba(239,68,68,0.8)', padding: '8px 16px',
              borderRadius: '8px', marginTop: '8px', fontSize: '0.85rem', fontWeight: '600'
            }}>🚫 {t.accountBlocked}</div>
          )}
        </div>

        {/* Prochain virement */}
        {balanceSummary?.next_payout_amount > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', background: 'rgba(99,102,241,0.2)', borderRadius: '10px',
            marginBottom: '16px', border: '1px solid rgba(99,102,241,0.3)'
          }}>
            <div>
              <p style={{ fontSize: '0.75rem', opacity: 0.7 }}>{t.nextPayout}</p>
              <p style={{ fontWeight: '600' }}>
                {balanceSummary.next_payout_date ? new Date(balanceSummary.next_payout_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}
              </p>
            </div>
            <p style={{ fontSize: '1.2rem', fontWeight: '700', color: '#FF9B00' }}>
              {(balanceSummary.next_payout_amount || 0).toLocaleString('fr-FR')} CDF
            </p>
          </div>
        )}

        {/* Résumé semaine */}
        <div style={{
          background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '12px' }}>📊 {t.weekSummary}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', textAlign: 'center' }}>
            <div>
              <p style={{ fontSize: '1.4rem', fontWeight: '700' }}>{balanceSummary?.week_summary?.rides_count || 0}</p>
              <p style={{ fontSize: '0.7rem', opacity: 0.7 }}>{t.rides}</p>
            </div>
            <div>
              <p style={{ fontSize: '1.4rem', fontWeight: '700', color: '#6ee7b7' }}>{(balanceSummary?.week_summary?.total_earned || 0).toLocaleString('fr-FR')}</p>
              <p style={{ fontSize: '0.7rem', opacity: 0.7 }}>{t.earnings}</p>
            </div>
            <div>
              <p style={{ fontSize: '1.4rem', fontWeight: '700', color: '#fcd34d' }}>{(balanceSummary?.week_summary?.total_commission || 0).toLocaleString('fr-FR')}</p>
              <p style={{ fontSize: '0.7rem', opacity: 0.7 }}>{t.commissions}</p>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          {[
            { key: 'transactions', label: t.transactionHistory },
            { key: 'rides', label: t.rides }
          ].map(tab => (
            <button key={tab.key} onClick={() => setBalTab(tab.key)} style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontSize: '0.85rem',
              fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
              background: balTab === tab.key ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
              color: 'white'
            }}>{tab.label}</button>
          ))}
        </div>

        {/* Filtre période */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '12px' }}>
          {[
            { key: 'week', label: t.week },
            { key: 'month', label: t.month },
            { key: 'year', label: t.year },
            { key: 'all', label: t.all }
          ].map(p => (
            <button key={p.key} onClick={() => setBalPeriod(p.key)} style={{
              padding: '6px', border: 'none', borderRadius: '6px', fontSize: '0.75rem',
              fontWeight: '600', cursor: 'pointer',
              background: balPeriod === p.key ? '#C9350F' : 'rgba(255,255,255,0.1)',
              color: 'white'
            }}>{p.label}</button>
          ))}
        </div>

        {/* Transactions */}
        {balTab === 'transactions' && (
          <div>
            {balanceTransactions.length > 0 ? balanceTransactions.map((tx, idx) => {
              const info = txTypeLabels[tx.type] || { label: tx.type, color: '#5C636E' };
              return (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', marginBottom: '6px'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.85rem' }}>{info.label}</div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                      {new Date(tx.created_at).toLocaleDateString('fr-FR')} {new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {tx.note && <div style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: '2px' }}>{tx.note}</div>}
                  </div>
                  <div style={{
                    fontWeight: '700', fontSize: '1rem',
                    color: tx.direction === 'credit' ? '#6ee7b7' : '#FEE2E2'
                  }}>
                    {tx.direction === 'credit' ? '+' : '-'}{tx.amount?.toLocaleString('fr-FR')} CDF
                  </div>
                </div>
              );
            }) : (
              <p style={{ textAlign: 'center', opacity: 0.5, padding: '2rem', fontSize: '0.85rem' }}>
                Aucune transaction
              </p>
            )}
          </div>
        )}

        {/* Courses */}
        {balTab === 'rides' && (
          <div>
            {balanceRidesList?.summary && (
              <div style={{
                background: 'rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px', marginBottom: '10px',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', textAlign: 'center', fontSize: '0.8rem'
              }}>
                <div>
                  <span style={{ opacity: 0.6 }}>{t.earnings}: </span>
                  <span style={{ fontWeight: '700', color: '#6ee7b7' }}>{(balanceRidesList.summary.total_driver_earnings || 0).toLocaleString('fr-FR')}</span>
                </div>
                <div>
                  <span style={{ opacity: 0.6 }}>{t.commissions}: </span>
                  <span style={{ fontWeight: '700', color: '#fcd34d' }}>{(balanceRidesList.summary.total_commission || 0).toLocaleString('fr-FR')}</span>
                </div>
              </div>
            )}
            {balanceRidesList?.rides && balanceRidesList.rides.length > 0 ? balanceRidesList.rides.map((r, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', marginBottom: '6px'
              }}>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.85rem' }}>
                    {r.ride_type?.charAt(0).toUpperCase() + r.ride_type?.slice(1)} — {r.payment_method === 'mobile_money' ? '📱' : '💵'}
                  </div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                    {new Date(r.created_at).toLocaleDateString('fr-FR')} {new Date(r.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '700', color: '#6ee7b7', fontSize: '0.9rem' }}>
                    {(r.driver_earnings || 0).toLocaleString('fr-FR')} CDF
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#fcd34d' }}>
                    -{((r.commission_base || 0) + (r.commission_night || 0)).toLocaleString('fr-FR')} comm.
                  </div>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', opacity: 0.5, padding: '2rem', fontSize: '0.85rem' }}>
                Aucune course sur cette période
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="driver-app">
      <IOSInstallBanner />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .driver-app {
          font-family: 'Poppins', sans-serif;
          min-height: 100vh;
          background: linear-gradient(135deg, #C9350F 0%, #C9350F 100%);
          position: relative;
        }

        .current-passengers-page {
          padding: 1.5rem;
          margin-bottom: 70px;
        }

        .mobile-header {
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(20px);
          padding: 1rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: white;
          font-weight: 700;
          font-size: 1.3rem;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .lang-selector {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
        }

        .menu-btn {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 0.5rem;
        }

        .mobile-menu {
          position: fixed;
          top: 0;
          right: ${menuOpen ? '0' : '-100%'};
          width: 80%;
          max-width: 300px;
          height: 100vh;
          background: rgba(10, 20, 60, 0.98);
          backdrop-filter: blur(20px);
          padding: 2rem 1.5rem;
          transition: right 0.3s ease;
          z-index: 200;
        }

        .menu-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: white;
          cursor: pointer;
        }

        .menu-nav {
          margin-top: 3rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .menu-item {
          padding: 1rem;
          color: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.2s;
          cursor: pointer;
        }

        .menu-item:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        .menu-item.active {
          background: rgba(30, 64, 175, 0.5);
        }

        .app-container {
          padding: 2rem 1.5rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .driver-card {
          background: white;
          border-radius: 24px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        }

        .driver-info {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .driver-avatar {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C9350F 0%, #C9350F 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 2rem;
          font-weight: 700;
        }

        .driver-info h2 {
          font-size: 1.3rem;
          color: #1C1C1C;
          margin-bottom: 0.3rem;
        }

        .vehicle-info {
          color: #5C636E;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .rating {
          margin-top: 0.5rem;
          font-size: 0.9rem;
          color: #FFB900;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.95);
          padding: 1.5rem;
          border-radius: 20px;
          display: flex;
          gap: 1rem;
          align-items: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-icon.earnings { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .stat-icon.trips { background: linear-gradient(135deg, #FF7800 0%, #F54119 100%); }
        .stat-icon.passengers { background: linear-gradient(135deg, #FFB900 0%, #FF9B00 100%); }

        .stat-value {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1C1C1C;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #5C636E;
          margin-top: 0.3rem;
        }

        .quick-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .action-btn {
          padding: 1.5rem;
          border: none;
          border-radius: 16px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: white;
          color: #C9350F;
          box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2);
        }

        .action-btn.secondary {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          backdrop-filter: blur(10px);
        }

        .action-btn:hover {
          transform: translateY(-3px);
        }

        .active-route-card {
          background: rgba(255, 255, 255, 0.95);
          padding: 1.5rem;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .active-route-card h3 {
          color: #10b981;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .route-name {
          color: #1C1C1C;
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .route-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #5C636E;
        }

        .scanner-page h2,
        .route-page h2 {
          color: white;
          font-size: 1.8rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .scanner-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
          width: 100%;
        }

        .scanner-init {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          padding: 2rem;
          text-align: center;
        }

        .scanner-init > svg {
          color: #10b981;
          opacity: 0.8;
        }

        .scanner-init p {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1.1rem;
        }

        .scanning-active {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          width: 100%;
        }

        #qr-reader {
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        }

        #qr-reader > div {
          border-radius: 15px !important;
        }

        .scanner-status {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255, 255, 255, 0.8);
        }

        .spinner {
          animation: spin 1s linear infinite;
          color: #10b981;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }

        .camera-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          padding: 2rem;
          background: rgba(220, 38, 38, 0.1);
          border-radius: 15px;
          border: 2px solid #dc2626;
          text-align: center;
          width: 100%;
        }

        .error-title {
          color: #dc2626;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .error-message {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        .troubleshooting {
          background: rgba(0, 0, 0, 0.3);
          padding: 1.5rem;
          border-radius: 10px;
          text-align: left;
          width: 100%;
        }

        .troubleshooting h4 {
          color: #FFB900;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .troubleshooting ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .troubleshooting li {
          color: rgba(255, 255, 255, 0.8);
          padding: 0.5rem 0;
          font-size: 0.95rem;
        }

        .scan-frame {
          width: 300px;
          height: 300px;
          position: relative;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .scan-frame.scanning {
          animation: pulse 1.5s infinite;
        }

        .corner {
          position: absolute;
          width: 40px;
          height: 40px;
          border: 4px solid white;
        }

        .corner.top-left { top: 10px; left: 10px; border-right: none; border-bottom: none; }
        .corner.top-right { top: 10px; right: 10px; border-left: none; border-bottom: none; }
        .corner.bottom-left { bottom: 10px; left: 10px; border-right: none; border-top: none; }
        .corner.bottom-right { bottom: 10px; right: 10px; border-left: none; border-top: none; }

        .scan-line {
          position: absolute;
          width: 80%;
          height: 3px;
          background: linear-gradient(90deg, transparent, #10b981, transparent);
          animation: scanning 2s infinite;
        }

        @keyframes scanning {
          0%, 100% { top: 20px; }
          50% { top: calc(100% - 20px); }
        }

        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
          50% { box-shadow: 0 0 0 20px rgba(16, 185, 129, 0); }
        }

        .scan-btn {
          background: white;
          color: #C9350F;
          padding: 1.2rem 2.5rem;
          border: none;
          border-radius: 50px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          box-shadow: 0 8px 30px rgba(255, 255, 255, 0.3);
          transition: all 0.2s;
        }

        .scan-btn:hover:not(:disabled) {
          transform: scale(1.05);
        }

        .scan-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .scan-btn.primary {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .scan-btn.secondary {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 2px solid white;
        }

        .scan-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .scan-result {
          display: flex;
          justify-content: center;
        }

        .result-card {
          background: white;
          border-radius: 24px;
          padding: 2rem;
          max-width: 400px;
          width: 100%;
          text-align: center;
        }

        .result-card.valid {
          border-top: 5px solid #10b981;
        }

        .result-card.invalid {
          border-top: 5px solid #DC2626;
        }

        .result-icon {
          margin-bottom: 1rem;
        }

        .result-icon svg {
          width: 60px;
          height: 60px;
        }

        .result-card.valid .result-icon svg {
          color: #10b981;
        }

        .result-card.invalid .result-icon svg {
          color: #DC2626;
        }

        .result-card h3 {
          color: #1C1C1C;
          font-size: 1.3rem;
          margin-bottom: 1.5rem;
        }

        .passenger-info {
          margin-bottom: 1.5rem;
        }

        .passenger-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1C1C1C;
          margin-bottom: 0.8rem;
        }

        .subscription-badge {
          display: inline-block;
          background: linear-gradient(135deg, #FFB900 0%, #FF9B00 100%);
          color: white;
          padding: 0.5rem 1.2rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .expiry-date,
        .qr-code {
          color: #5C636E;
          font-size: 0.9rem;
          margin-top: 0.5rem;
        }

        .error-reason {
          background: rgba(220, 38, 38, 0.1);
          border-left: 4px solid #dc2626;
          color: #FEE2E2;
          padding: 1rem;
          border-radius: 8px;
          margin: 1rem 0;
          font-size: 0.95rem;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .btn-accept,
        .btn-refuse {
          flex: 1;
          padding: 1rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-accept {
          background: #10b981;
          color: white;
        }

        .btn-refuse {
          background: #DC2626;
          color: white;
        }

        .btn-scan-another {
          width: 100%;
          padding: 1rem;
          background: #F4F5F7;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          color: #414852;
        }

        .route-subtitle {
          color: rgba(255, 255, 255, 0.9);
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .routes-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .route-card {
          background: white;
          border-radius: 16px;
          padding: 1.5rem;
          display: flex;
          gap: 1rem;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .route-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
        }

        .route-card svg {
          color: #C9350F;
        }

        .route-details {
          flex: 1;
        }

        .route-details h3 {
          color: #1C1C1C;
          font-size: 1.05rem;
          margin-bottom: 0.5rem;
        }

        .route-meta {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          color: #5C636E;
          font-size: 0.85rem;
        }

        .price {
          background: #10b981;
          color: white;
          padding: 0.2rem 0.6rem;
          border-radius: 12px;
          font-weight: 600;
        }

        .active-route {
          background: white;
          border-radius: 24px;
          padding: 2rem;
        }

        .route-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #F4F5F7;
        }

        .route-header h3 {
          color: #1C1C1C;
          font-size: 1.2rem;
        }

        .btn-end-route {
          background: #DC2626;
          color: white;
          padding: 0.6rem 1.2rem;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          font-size: 0.9rem;
        }

        .route-stats {
          display: flex;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .route-stats .stat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #414852;
          font-weight: 600;
        }

        .seats-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .seat {
          aspect-ratio: 1;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .seat.available {
          background: #E9EBEF;
          color: #5C636E;
        }

        .seat.occupied {
          background: #10b981;
          color: white;
        }

        .seats-info {
          text-align: center;
          color: #5C636E;
          font-weight: 600;
        }

        /* Styles d'authentification */
        .auth-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #C9350F 0%, #C9350F 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .auth-container {
          width: 100%;
          max-width: 450px;
        }

        .driver-register .auth-card {
          max-width: 550px;
        }

        .auth-card {
          background: white;
          border-radius: 24px;
          padding: 3rem 2.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .auth-card h1 {
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 0.5rem;
          color: #C9350F;
        }

        .auth-card h2 {
          font-size: 1.8rem;
          text-align: center;
          margin-bottom: 0.5rem;
          color: #1C1C1C;
        }

        .auth-subtitle {
          text-align: center;
          color: #5C636E;
          margin-bottom: 2rem;
        }

        .steps-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          padding: 1rem 0;
        }

        .step {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #E9EBEF;
          color: #9AA0A8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.3s;
        }

        .step.active {
          background: linear-gradient(135deg, #C9350F 0%, #C9350F 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(30, 64, 175, 0.3);
        }

        .step-line {
          width: 60px;
          height: 3px;
          background: #E9EBEF;
          margin: 0 0.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          color: #414852;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .required {
          color: #dc2626;
          font-weight: bold;
        }

        .optional {
          color: #9AA0A8;
          font-weight: normal;
          font-size: 0.85rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.9rem 1rem;
          border: 2px solid #E9EBEF;
          border-radius: 12px;
          font-size: 1rem;
          font-family: 'Poppins', sans-serif;
          transition: all 0.2s;
        }

        .phone-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .phone-input-wrapper input {
          padding-left: 3rem;
        }

        .phone-icon {
          position: absolute;
          left: 1rem;
          color: #5C636E;
        }

        .input-hint {
          display: block;
          margin-top: 0.4rem;
          color: #5C636E;
          font-size: 0.8rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #C9350F;
          box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
        }

        .photo-capture-area {
          margin-top: 0.5rem;
        }

        .photo-placeholder,
        .document-placeholder {
          border: 3px dashed #DCE0E6;
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s;
          background: #FFFFFF;
        }

        .photo-placeholder:hover,
        .document-placeholder:hover {
          border-color: #C9350F;
          background: #F4F5F7;
        }

        .photo-placeholder svg,
        .document-placeholder svg {
          color: #9AA0A8;
          margin-bottom: 1rem;
        }

        .photo-placeholder p,
        .document-placeholder p {
          color: #414852;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .photo-placeholder small,
        .document-placeholder small {
          color: #5C636E;
          font-size: 0.8rem;
        }

        .photo-preview,
        .document-preview {
          text-align: center;
        }

        .photo-preview img {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #C9350F;
          margin-bottom: 1rem;
          box-shadow: 0 4px 15px rgba(30, 64, 175, 0.2);
        }

        .document-preview img {
          width: 100%;
          max-width: 400px;
          height: auto;
          border-radius: 12px;
          border: 3px solid #C9350F;
          margin-bottom: 1rem;
          box-shadow: 0 4px 15px rgba(30, 64, 175, 0.2);
        }

        .change-photo-btn {
          background: #F4F5F7;
          border: 2px solid #E9EBEF;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          color: #414852;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Poppins', sans-serif;
        }

        .change-photo-btn:hover {
          background: #E9EBEF;
          border-color: #C9350F;
          color: #C9350F;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .form-actions button {
          flex: 1;
        }

        .btn-secondary {
          background: #F4F5F7;
          color: #414852;
          border: 2px solid #E9EBEF;
          padding: 1rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Poppins', sans-serif;
        }

        .btn-secondary:hover {
          background: #E9EBEF;
          border-color: #C9350F;
          color: #C9350F;
        }

        .btn-auth {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #C9350F 0%, #C9350F 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Poppins', sans-serif;
          margin-top: 1rem;
        }

        .btn-auth:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(30, 64, 175, 0.4);
        }

        .auth-switch {
          text-align: center;
          margin-top: 1.5rem;
          color: #5C636E;
        }

        .auth-switch span {
          color: #C9350F;
          font-weight: 600;
          cursor: pointer;
          text-decoration: underline;
        }

        .error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.8rem 1rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .pending-approval {
          background: linear-gradient(135deg, #FFB900 0%, #FF9B00 100%);
          color: white;
          padding: 2.5rem 2rem;
          border-radius: 24px;
          text-align: center;
          margin: 2rem 0;
          box-shadow: 0 10px 40px rgba(245, 158, 11, 0.3);
        }

        .pending-approval h3 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .pending-approval p {
          opacity: 0.95;
          line-height: 1.6;
          font-size: 1.05rem;
        }

        @media (min-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
      `}</style>

      {isAuthenticated && (
        <div className="mobile-header">
          <div className="logo">
            <Car size={28} />
            Mbiyo Chauffeur
          </div>
          <div className="header-actions">
            <select 
              className="lang-selector" 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="sw">SW</option>
              <option value="ln">LN</option>
            </select>
            <button className="menu-btn" onClick={() => setMenuOpen(true)}>
              <Menu size={24} />
            </button>
          </div>
        </div>
      )}

      {isAuthenticated && (
        <div className="mobile-menu">
          <button className="menu-close" onClick={() => setMenuOpen(false)}>
            <X size={24} />
          </button>
          <nav className="menu-nav">
            <div 
              className={`menu-item ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('home'); setMenuOpen(false); }}
            >
              <MapPin size={20} />
              {t.home}
            </div>
            <div 
              className={`menu-item ${currentPage === 'scanner' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('scanner'); setMenuOpen(false); }}
            >
              <Camera size={20} />
              {t.scanner}
            </div>
            <div 
              className={`menu-item ${currentPage === 'route' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('route'); setMenuOpen(false); }}
            >
              <MapPin size={20} />
              {t.myRoute}
            </div>
            <div 
              className={`menu-item ${currentPage === 'currentPassengers' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('currentPassengers'); setMenuOpen(false); }}
            >
              <Users size={20} />
              Passagers à bord
            </div>
            <div 
              className={`menu-item ${currentPage === 'profile' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('profile'); setMenuOpen(false); }}
            >
              <Settings size={20} />
              Mon Profil
            </div>
            <div 
              className={`menu-item ${currentPage === 'taxi-driver' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('taxi-driver'); setMenuOpen(false); }}
            >
              <Car size={20} />
              {t.taxiRides}
            </div>
            <div 
              className={`menu-item ${currentPage === 'my-balance' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('my-balance'); setMenuOpen(false); }}
            >
              <span style={{ fontSize: '20px' }}>💰</span>
              {t.myBalance}
            </div>
            <div 
              className="menu-item"
              onClick={() => {
                authServiceDriver.logout();
                setIsAuthenticated(false);
                setCurrentUser(null);
                setCurrentPage('login');
                setMenuOpen(false);
              }}
              style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '1rem', paddingTop: '1rem' }}
            >
              <XCircle size={20} />
              Déconnexion
            </div>
          </nav>
        </div>
      )}

      <div className="app-container">
        {/* === NOTIFICATION FLOTTANTE NOUVELLE COURSE === */}
        {showRideNotification && notificationRide && isDriverAvailable && currentPage !== 'taxi-driver' && (
          <div
            onClick={goToTaxiFromNotification}
            style={{
              position: 'fixed',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'calc(100% - 20px)',
              maxWidth: '420px',
              background: 'linear-gradient(135deg, #FFB900, #FF9B00)',
              color: 'white',
              borderRadius: '16px',
              padding: '14px 16px',
              boxShadow: '0 8px 32px rgba(245,158,11,0.4)',
              zIndex: 10000,
              cursor: 'pointer',
              animation: 'slideDown 0.4s ease-out',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{
              background: 'rgba(255,255,255,0.25)',
              borderRadius: '12px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              <Car size={24} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '700', fontSize: '0.95rem', marginBottom: '2px' }}>
                🚕 {notificationRide.ride_type_label || 'Nouvelle course disponible !'}
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                {notificationRide.pickup?.address?.substring(0, 40) || 'Course en attente'}...
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', marginTop: '2px' }}>
                {notificationRide.estimated_price?.toLocaleString('fr-FR')} CDF
              </div>
            </div>
            <div style={{
              background: 'white',
              color: '#FF9B00',
              borderRadius: '10px',
              padding: '8px 12px',
              fontSize: '0.8rem',
              fontWeight: '700',
              whiteSpace: 'nowrap'
            }}>
              VOIR →
            </div>
          </div>
        )}

        {/* Badge courses disponibles sur toutes les pages */}
        {pendingRidesCount > 0 && isDriverAvailable && currentPage !== 'taxi-driver' && isAuthenticated && currentUser?.status === 'approved' && (
          <div
            onClick={goToTaxiFromNotification}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '16px',
              background: 'linear-gradient(135deg, #FFB900, #FF9B00)',
              color: 'white',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(245,158,11,0.5)',
              zIndex: 9999,
              cursor: 'pointer',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          >
            <Car size={22} />
            <span style={{ fontSize: '0.65rem', fontWeight: '800', marginTop: '-2px' }}>{pendingRidesCount}</span>
          </div>
        )}

        <style>{`
          @keyframes slideDown {
            from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
            to { transform: translateX(-50%) translateY(0); opacity: 1; }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>

        {currentPage === 'login' && <Stable render={LoginPage} key="LoginPage" />}
        {currentPage === 'register' && <Stable render={RegisterPage} key="RegisterPage" />}
        {currentPage === 'forgot-password' && <Stable render={ForgotPasswordPage} key="ForgotPasswordPage" />}
        {currentPage === 'pending' && <Stable render={PendingPage} key="PendingPage" />}
        {currentPage === 'home' && <Stable render={HomePage} key="HomePage" />}
        {currentPage === 'scanner' && <Stable render={ScannerPage} key="ScannerPage" />}
        {currentPage === 'route' && <Stable render={RoutePage} key="RoutePage" />}
        {currentPage === 'currentPassengers' && <Stable render={CurrentPassengersPage} key="CurrentPassengersPage" />}
        {currentPage === 'profile' && <Stable render={ProfilePage} key="ProfilePage" />}
        {currentPage === 'taxi-driver' && <Stable render={TaxiDriverPage} key="TaxiDriverPage" />}
        {currentPage === 'my-balance' && <Stable render={MyBalancePage} key="MyBalancePage" />}
      </div>


    </div>
  );
}